import { useState, useEffect, useCallback, useRef, useMemo } from "react";
import {
  useAccount,
  useBalance,
  useConnect,
  useDisconnect,
  useSwitchChain,
  useWriteContract,
  useSendTransaction,
} from "wagmi";
import {
  createPublicClient,
  http,
  fallback,
  parseUnits,
  formatUnits,
  decodeErrorResult,
} from "viem";
import { waitForTransactionReceipt } from "viem/actions";
import { AnimatePresence, motion } from "framer-motion";
import clsx from "clsx";
import axios from "axios";
import toast from "react-hot-toast";
import { v4 as uuidv4 } from "uuid";
import {
  ArrowRightLeft,
  ChevronDown,
  CheckCircle2,
  ExternalLink,
  Wallet,
  X,
} from "lucide-react";
import {
  AVAILABLE_TOKEN_KEYS,
  BRIDGE_TOKENS,
  CHAIN_IDS,
  CHAIN_LABELS,
  CHAIN_SHORT_LABELS,
  getExplorerLink,
  getRouteConfig,
  getRouteParts,
  bridgeWrappedTokenABI,
  erc20TransferABI,
} from "./bridgeConfig";
import { injectedConnector } from "../walletConfig";
import { useXrpWallet } from "../hooks/useXrpWallet";
import { useWalletConnectXrp } from "../hooks/useWalletConnectXrp";
import { useSolWallet } from "../hooks/useSolWallet";

const GAS_LIMIT = 100000n;
const MAX_RETRIES = 10;
const RETRY_DELAY = 5000;
const BRIDGE_FEE_BPS = 10n;
const BRIDGE_FEE_DENOMINATOR = 10000n;
const XRP_MIN_DROPS = 10_000_000n;
const SOL_MIN_LAMPORTS = 50_000_000n; // 0.05 SOL
const SOL_ADDRESS_REGEX = /^[1-9A-HJ-NP-Za-km-z]{32,44}$/;
const XRP_RADDRESS_REGEX = /^r[1-9A-HJ-NP-Za-km-z]{24,34}$/;
const EMPTY_ROUTE_LIST = [];
const SOURCE_RECEIPT_TIMEOUT_MS = {
  ETH: 45 * 60 * 1000,
  BSC: 15 * 60 * 1000,
  BESC: 15 * 60 * 1000,
  RH: 15 * 60 * 1000,
};
const SOURCE_RECEIPT_POLL_MS = {
  ETH: 4000,
  BSC: 1000,
  BESC: 1000,
  RH: 1000,
};
const SUCCESS_CONFETTI_PIECES = Array.from({ length: 18 }, (_, index) => ({
  id: index,
  left: `${4 + index * 5.2}%`,
  delay: `${(index % 6) * 0.08}s`,
  duration: `${2.4 + (index % 4) * 0.22}s`,
  rotate: `${-24 + index * 7}deg`,
}));

const EXECUTION_STEPS = [
  { key: "signature", title: "Approve in Wallet" },
  { key: "submitted", title: "Transaction Submitted" },
  { key: "confirming", title: "Waiting for Source Confirmation" },
  { key: "processing", title: "Processing Bridge Transfer" },
  { key: "delivered", title: "Delivered on Destination Chain" },
];

const getAssetCoverageLabel = (tokenEntry) => {
  const chainKeys = Object.keys(tokenEntry?.chains || {});
  return chainKeys.map((chain) => CHAIN_SHORT_LABELS[chain]).join(" • ");
};

const getAssetRouteCountLabel = (tokenEntry) => `${tokenEntry?.routes?.length || 0} routes`;

const shortValue = (value) => (value ? `${value.slice(0, 6)}...${value.slice(-4)}` : "");

const formatDisplayNumber = (value, maxFractionDigits = 6) => {
  const numericValue = Number(value);
  if (!Number.isFinite(numericValue)) {
    return "0";
  }

  return new Intl.NumberFormat("en-US", {
    minimumFractionDigits: 0,
    maximumFractionDigits: maxFractionDigits,
  }).format(numericValue);
};

const formatDisplayUnits = (value, decimals, maxFractionDigits = 6) => formatDisplayNumber(formatUnits(value, decimals), maxFractionDigits);

const getBridgeQuote = (amount, sourceAsset, destAsset, tokenKey, token) => {
  if (!amount || !sourceAsset || !destAsset) {
    return null;
  }

  const sourceAmountRaw = parseUnits(amount.toString(), sourceAsset.decimals);

  // Reflection tokens emit post-tax amount in Transfer event; adjust for accurate fee preview
  const taxBps = BigInt(sourceAsset.sourceTokenTaxBps || 0);
  const taxRaw = (sourceAmountRaw * taxBps) / BRIDGE_FEE_DENOMINATOR;
  const postTaxAmountRaw = sourceAmountRaw - taxRaw;

  const effectiveFeeBps = token?.bridgeFeeBps ? BigInt(token.bridgeFeeBps) : BRIDGE_FEE_BPS;
  const feeRaw = tokenKey === "WBESC" && sourceAsset.mode === "native"
    ? 0n
    : (postTaxAmountRaw * effectiveFeeBps) / BRIDGE_FEE_DENOMINATOR;
  const netSourceRaw = postTaxAmountRaw - feeRaw;
  const scaledDestRaw =
    sourceAsset.decimals === destAsset.decimals
      ? netSourceRaw
      : destAsset.decimals > sourceAsset.decimals
        ? netSourceRaw * 10n ** BigInt(destAsset.decimals - sourceAsset.decimals)
        : netSourceRaw / 10n ** BigInt(sourceAsset.decimals - destAsset.decimals);

  // Reflection tax on the destination chain (e.g. HOLD on ETH taxes outgoing transfers too)
  const destTaxBps = BigInt(destAsset.destTokenTaxBps || 0);
  const destTaxRaw = (scaledDestRaw * destTaxBps) / BRIDGE_FEE_DENOMINATOR;
  const netDestRaw = scaledDestRaw - destTaxRaw;

  return {
    taxRaw,
    feeRaw,
    destTaxRaw,
    netSourceRaw,
    netDestRaw,
    formattedReceive: formatDisplayUnits(netDestRaw, destAsset.decimals),
  };
};

const getRouteActionLabel = (asset, role) => {
  if (!asset) {
    return "";
  }

  if (role === "source") {
    if (asset.mode === "native" || asset.mode === "xrp-native" || asset.mode === "sol-native") return `Send ${asset.symbol}`;
    if (asset.mode === "sol-token") return `Burn ${asset.symbol}`;
    return `Use ${asset.symbol}`;
  }

  return `Receive ${asset.symbol}`;
};

const createProgressState = () => ({
  open: false,
  stepKey: "signature",
  headline: "",
  detail: "",
  tone: "info",
  sourceTxHash: "",
  destinationTxHash: "",
});

const modalTransition = {
  type: "spring",
  stiffness: 230,
  damping: 24,
};

const isProgressClosable = (progress) => {
  if (!progress?.open) {
    return true;
  }

  return progress.tone === "error" || progress.stepKey === "delivered";
};

const publicClients = {
  eth: createPublicClient({
    chain: { id: CHAIN_IDS.ETH, name: "Ethereum" },
    transport: http(process.env.REACT_APP_RPC_ETH, { retryCount: 3, retryDelay: 1000, timeout: 30000 }),
  }),
  bsc: createPublicClient({
    chain: { id: CHAIN_IDS.BSC, name: "BNB Smart Chain" },
    transport: http(process.env.REACT_APP_RPC_BSC),
  }),
  besc: createPublicClient({
    chain: {
      id: CHAIN_IDS.BESC,
      name: "BESC HyperChain",
      network: "besc",
      nativeCurrency: { name: "BESC", symbol: "BESC", decimals: 18 },
      rpcUrls: { default: { http: ["https://hyper.beschyperchain.com", "https://rpc.beschyperchain.com"] } },
      blockExplorers: { default: { name: "BESC Explorer", url: "https://explorer.beschyperchain.com" } },
    },
    transport: fallback([
      http("https://hyper.beschyperchain.com", { timeout: 10000 }),
      http("https://rpc.beschyperchain.com", { timeout: 10000 }),
    ]),
  }),
};

const BridgeProgressModal = ({
  progress,
  sourceChain,
  destChain,
  sourceLabel,
  destLabel,
  tokenLabel,
  sourceSymbol,
  destinationSymbol,
  amountText,
  receiveText,
  onClose,
}) => {
  const activeStepIndex = Math.max(
    0,
    EXECUTION_STEPS.findIndex((step) => step.key === progress.stepKey)
  );
  const progressPercent = (activeStepIndex / (EXECUTION_STEPS.length - 1)) * 100;
  const primaryExplorerLink = getExplorerLink(destChain, progress.destinationTxHash) || getExplorerLink(sourceChain, progress.sourceTxHash);
  const canClose = isProgressClosable(progress);
  const progressToneLabel =
    progress.tone === "error" ? "Action needed" : progress.stepKey === "delivered" ? "Transfer complete" : "Bridge in progress";
  const progressLiveLabel =
    progress.tone === "error" ? "Needs review" : progress.stepKey === "delivered" ? "Completed" : "Live updates";

  return (
    <motion.div
      className="bridge-modal-overlay"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.2 }}
    >
      <motion.div
        className="bridge-modal-card bridge-modal-card--progress"
        initial={{ opacity: 0, y: 24, scale: 0.98 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        exit={{ opacity: 0, y: 16, scale: 0.98 }}
        transition={modalTransition}
      >
        {canClose ? (
          <button type="button" className="bridge-modal-close" onClick={onClose} aria-label="Close progress panel">
            <X size={18} />
          </button>
        ) : null}

        <div className="bridge-modal-card__head">
          <span className={clsx("bridge-modal-pill", `is-${progress.tone}`)}>
            {progressToneLabel}
          </span>
          <div className="bridge-progress-live">
            <span className="bridge-progress-live__dot" />
            <span>{progressLiveLabel}</span>
          </div>
          <h3>{progress.headline || EXECUTION_STEPS[activeStepIndex].title}</h3>
          <p>{progress.detail || "Your transfer is being updated live as it moves through each stage."}</p>
        </div>

        <div className="bridge-modal-summary bridge-modal-summary--progress">
          <div className="bridge-modal-summary__route">
            <span className="bridge-modal-summary__eyebrow">Route</span>
            <div className="bridge-modal-summary__route-line">
              <strong>{sourceLabel}</strong>
              <span className="bridge-modal-summary__route-arrow">to</span>
              <strong>{destLabel}</strong>
            </div>
            <small>{tokenLabel}</small>
          </div>
          <div className="bridge-modal-summary__metrics">
            <div className="bridge-modal-summary__metric">
              <span>Sending</span>
              <strong>{amountText} {sourceSymbol}</strong>
            </div>
            <div className="bridge-modal-summary__metric">
              <span>Estimated receive</span>
              <strong>{receiveText} {destinationSymbol}</strong>
            </div>
            <div className="bridge-modal-summary__metric">
              <span>Status</span>
              <strong>{progressToneLabel}</strong>
            </div>
          </div>
        </div>

        <div className="bridge-progress">
          <div className="bridge-progress__track">
            <div className="bridge-progress__track-glow" />
            <motion.div
              className={clsx("bridge-progress__bar", `is-${progress.tone}`)}
              animate={{ width: `${progressPercent}%` }}
              transition={{ duration: 0.35, ease: "easeOut" }}
            />
          </div>

          <div className="bridge-progress__legend">
            <span>Bridge timeline</span>
            <strong>{Math.round(progressPercent)}%</strong>
          </div>

          <div className="bridge-progress__steps">
            {EXECUTION_STEPS.map((step, index) => {
              const state =
                index < activeStepIndex ? "completed" : index === activeStepIndex ? (progress.tone === "error" ? "error" : "active") : "pending";

              return (
                <div key={step.key} className={clsx("bridge-progress-step", `is-${state}`)}>
                  <span className="bridge-progress-step__marker">
                    {state === "completed" || (progress.stepKey === "delivered" && index === activeStepIndex) ? <CheckCircle2 size={14} /> : index + 1}
                  </span>
                  <div>
                    <strong>{step.title}</strong>
                    <p>{state === "completed" ? "Complete" : state === "active" ? "In progress" : state === "error" ? "Review required" : "Queued"}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <div className="bridge-modal-card__meta">
          {progress.sourceTxHash && (
            <a href={getExplorerLink(sourceChain, progress.sourceTxHash)} target="_blank" rel="noopener noreferrer" className="bridge-modal-link">
              View source transaction
              <ExternalLink size={14} />
            </a>
          )}
          {progress.destinationTxHash && (
            <a href={getExplorerLink(destChain, progress.destinationTxHash)} target="_blank" rel="noopener noreferrer" className="bridge-modal-link">
              View destination transaction
              <ExternalLink size={14} />
            </a>
          )}
          {!progress.destinationTxHash && primaryExplorerLink && progress.stepKey === "delivered" && (
            <a href={primaryExplorerLink} target="_blank" rel="noopener noreferrer" className="bridge-modal-link">
              View bridge explorer
              <ExternalLink size={14} />
            </a>
          )}
        </div>
      </motion.div>
    </motion.div>
  );
};

const BridgeSuccessModal = ({ successState, onRestart, onClose }) => {
  if (!successState) {
    return null;
  }

  const explorerLabel = successState.destinationTxHash ? "View destination explorer" : "View tracking transaction";

  return (
    <motion.div
      className="bridge-modal-overlay"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.22 }}
    >
      <motion.div
        className="bridge-modal-card bridge-modal-card--success"
        initial={{ opacity: 0, y: 24, scale: 0.98 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        exit={{ opacity: 0, y: 16, scale: 0.98 }}
        transition={modalTransition}
      >
        <div className="bridge-success-confetti" aria-hidden="true">
          {SUCCESS_CONFETTI_PIECES.map((piece) => (
            <span
              key={piece.id}
              className="bridge-success-confetti__piece"
              style={{
                left: piece.left,
                animationDelay: piece.delay,
                animationDuration: piece.duration,
                transform: `rotate(${piece.rotate})`,
              }}
            />
          ))}
        </div>

        <button type="button" className="bridge-modal-close" onClick={onClose} aria-label="Close success panel">
          <X size={18} />
        </button>

        <div className="bridge-success-ring">
          <CheckCircle2 size={26} />
        </div>

        <div className="bridge-modal-card__head bridge-modal-card__head--centered">
          <span className="bridge-modal-pill is-success">Bridge Complete</span>
          <h3>Assets delivered successfully.</h3>
          <p>Your transfer finalized and the destination leg is now available on the selected network.</p>
        </div>

        <div className="bridge-success-grid">
          <div className="bridge-success-item">
            <span>Amount sent</span>
            <strong>
              {successState.amountSent} {successState.sourceSymbol}
            </strong>
          </div>
          <div className="bridge-success-item">
            <span>Amount received</span>
            <strong>
              {successState.amountReceived} {successState.destinationSymbol}
            </strong>
          </div>
          <div className="bridge-success-item">
            <span>Destination chain</span>
            <strong>{successState.destinationLabel}</strong>
          </div>
          <div className="bridge-success-item">
            <span>Transaction hash</span>
            <strong>{shortValue(successState.destinationTxHash || successState.sourceTxHash)}</strong>
          </div>
        </div>

        <div className="bridge-modal-card__meta bridge-modal-card__meta--stacked">
          <span className="bridge-hash-line">{successState.destinationTxHash || successState.sourceTxHash}</span>
        </div>

        <div className="bridge-confirm-actions bridge-confirm-actions--success">
          {successState.explorerLink && (
            <a href={successState.explorerLink} target="_blank" rel="noopener noreferrer" className="secondary-button premium-secondary-button">
              {explorerLabel}
            </a>
          )}
          <button type="button" className="primary-button premium-primary-button" onClick={onRestart}>
            Bridge Again
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
};

const CompactSelect = ({
  label,
  value,
  detail = "",
  options,
  isOpen,
  onToggle,
  onSelect,
  disabled = false,
  menuClassName = "",
}) => (
  <div className={clsx("premium-select", isOpen && "is-open")}>
    <button type="button" className="premium-select__trigger" onClick={onToggle} disabled={disabled}>
      <span className="premium-select__label">{label}</span>
      <span className="premium-select__value">
        <strong>{value}</strong>
        {detail ? <small>{detail}</small> : null}
      </span>
      <span className="premium-select__chevron">
        <ChevronDown size={17} />
      </span>
    </button>

    <AnimatePresence>
      {isOpen ? (
        <motion.div
          className={clsx("premium-select__menu", menuClassName)}
          initial={{ opacity: 0, y: 8, scale: 0.98 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: 6, scale: 0.98 }}
          transition={{ duration: 0.18, ease: "easeOut" }}
        >
          {options.map((option) => (
            <button
              key={option.key}
              type="button"
              className={clsx("premium-select__option", option.active && "is-active")}
              onClick={() => onSelect(option.key)}
            >
              <span className="premium-select__option-main">
                <strong>{option.title}</strong>
                {option.detail ? <small>{option.detail}</small> : null}
              </span>
              {option.meta ? <span className="premium-select__option-meta">{option.meta}</span> : null}
            </button>
          ))}
        </motion.div>
      ) : null}
    </AnimatePresence>
  </div>
);

const BridgeWidget = () => {
  // Dynamically-registered tokens fetched from /tokens endpoint at mount time.
  // Merged with static BRIDGE_TOKENS so the token selector shows custom assets
  // without requiring a frontend rebuild after each admin registration.
  const [customTokenMap, setCustomTokenMap] = useState({});

  const allTokens = useMemo(() => ({ ...BRIDGE_TOKENS, ...customTokenMap }), [customTokenMap]);
  const allTokenKeys = useMemo(
    () => Object.entries(allTokens).filter(([, t]) => t.available).map(([k]) => k),
    [allTokens]
  );

  // Fetch custom tokens from backend on mount and build BRIDGE_TOKENS-compatible entries
  useEffect(() => {
    const buildCustomEntry = (t) => {
      const chains = {};
      const src = t.sourceChain; // "SOL", "ETH", "BSC", "RH"

      if (src === "SOL") {
        chains[src] = {
          mode: "sol-spl-send",
          symbol: t.symbol,
          decimals: t.decimals,
          mintAddress: t.sourceAddress,
          bridgeWalletAddress: process.env.REACT_APP_SOL_BRIDGE_WALLET_ADDRESS,
        };
      } else {
        // ETH / BSC / RH: user sends ERC20 to bridge receiver wallet
        chains[src] = {
          mode: "erc20-send",
          symbol: t.symbol,
          decimals: t.decimals,
          address: t.sourceAddress,
          bridgeReceiverAddress: process.env.REACT_APP_ERC20_BRIDGE_RECEIVER,
          abi: erc20TransferABI,
          checks: {},
        };
      }

      chains.BESC = {
        mode: "token",
        symbol: t.symbol,
        decimals: t.decimals,
        address: t.bescWrappedAddress,
        abi: bridgeWrappedTokenABI,
        checks: {},
      };

      return {
        apiToken: t.apiToken,
        label: t.label,
        bridgeFeeBps: t.bridgeFeeBps ?? 10,
        available: true,
        routes: t.routes,
        chains,
      };
    };

    axios({
      method: "get",
      url: `${process.env.REACT_APP_BACKEND_URL}/tokens`,
      headers: { "x-api-key": process.env.REACT_APP_API_KEY },
    })
      .then((res) => {
        const map = {};
        for (const t of res.data.tokens || []) {
          map[t.apiToken] = buildCustomEntry(t);
        }
        setCustomTokenMap(map);
      })
      .catch(() => {}); // silent — fallback to static token list
  }, []);

  const defaultTokenKey = AVAILABLE_TOKEN_KEYS[0] || "WBESC";
  const defaultDirection = BRIDGE_TOKENS[defaultTokenKey]?.routes[0] || "ETHtoBSC";

  const [amount, setAmount] = useState("");
  const [netAmount, setNetAmount] = useState("");
  const [bridgeQuote, setBridgeQuote] = useState(null);
  const [tokenKey, setTokenKey] = useState(() => {
    try {
      for (const key of ['xaman_restore', 'xaman_pending']) {
        const stored = sessionStorage.getItem(key);
        if (stored) {
          const { token } = JSON.parse(stored);
          if (token && BRIDGE_TOKENS[token]) return token;
        }
      }
    } catch {}
    return defaultTokenKey;
  });
  const [direction, setDirection] = useState(() => {
    try {
      for (const key of ['xaman_restore', 'xaman_pending']) {
        const stored = sessionStorage.getItem(key);
        if (stored) {
          const { token, direction: dir } = JSON.parse(stored);
          if (token && dir && BRIDGE_TOKENS[token]?.routes.includes(dir)) return dir;
        }
      }
    } catch {}
    return defaultDirection;
  });
  const [status, setRawStatus] = useState("");
  const [statusTone, setStatusTone] = useState("info");
  const [statusDetail, setStatusDetail] = useState("");
  const [txHash, setTxHash] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [amountError, setAmountError] = useState("");
  const [showBridgeConfirm, setShowBridgeConfirm] = useState(false);
  const [bridgeProgress, setBridgeProgress] = useState(createProgressState);
  const [successState, setSuccessState] = useState(null);
  const [bridgeContext, setBridgeContext] = useState(null);
  const [activeSelector, setActiveSelector] = useState(null);
  const amountInputRef = useRef(null);
  const hasMountedRef = useRef(false);
  const selectorRegionRef = useRef(null);
  const bridgeSubmitLockRef = useRef(false);
  const xamanLinkRef = useRef(null);

  // XRP-specific state
  const [xrpWalletPickerOpen, setXrpWalletPickerOpen] = useState(false);
  const [xrpDestAddress, setXrpDestAddress] = useState("");
  const [xrpDestError, setXrpDestError] = useState("");
  const [bescDestOverride, setBescDestOverride] = useState("");
  const [xamanSigningUrl, setXamanSigningUrl] = useState(null);
  // SOL-specific state
  const [solDestAddress, setSolDestAddress] = useState("");
  const [solDestError, setSolDestError] = useState("");
  const [bescDestSolOverride, setBescDestSolOverride] = useState("");

  const showStatus = useCallback((message, { tone = "info", detail = "" } = {}) => {
    setRawStatus(message);
    setStatusTone(tone);
    setStatusDetail(detail);
  }, []);

  const clearStatus = useCallback(() => showStatus("", { tone: "info", detail: "" }), [showStatus]);
  const showInfoStatus = useCallback((message, detail = "") => showStatus(message, { tone: "info", detail }), [showStatus]);
  const showSuccessStatus = useCallback((message, detail = "") => showStatus(message, { tone: "success", detail }), [showStatus]);
  const showErrorStatus = useCallback((message, detail = "") => showStatus(message, { tone: "error", detail }), [showStatus]);

  const setProgressStep = useCallback((stepKey, headline, detail = "", extras = {}) => {
    setBridgeProgress((previous) => ({
      open: true,
      stepKey,
      headline,
      detail,
      tone: extras.tone || (stepKey === "delivered" ? "success" : "info"),
      sourceTxHash: extras.sourceTxHash ?? previous.sourceTxHash ?? "",
      destinationTxHash: extras.destinationTxHash ?? previous.destinationTxHash ?? "",
    }));
  }, []);

  const showProgressError = useCallback((headline, detail = "") => {
    setBridgeProgress((previous) =>
      previous.open
        ? {
            ...previous,
            tone: "error",
            headline,
            detail,
          }
        : previous
    );
  }, []);

  const scaleAmount = useCallback((value, fromDecimals, toDecimals) => {
    if (value === null || value === undefined) return null;
    if (fromDecimals === toDecimals) return BigInt(value);
    const delta = toDecimals - fromDecimals;
    if (delta > 0) {
      return BigInt(value) * 10n ** BigInt(delta);
    }
    return BigInt(value) / 10n ** BigInt(Math.abs(delta));
  }, []);

  const getFriendlyError = useCallback((error, fallbackMessage = "Something went wrong. Please try again or contact support.") => {
    if (!error) {
      return { message: fallbackMessage, detail: "" };
    }

    const rawDetail =
      typeof error === "string"
        ? error
        : error?.response?.data?.details ?? error?.response?.data?.message ?? error?.message ?? "";
    const cleanedDetail = rawDetail?.toString?.().trim?.() ?? "";
    const normalizedDetail = cleanedDetail.toLowerCase?.() ?? "";
    const statusCode = error?.response?.status;

    if (error?.code === 4001 || normalizedDetail.includes("user rejected") || normalizedDetail.includes("rejected by user")) {
      return {
        message: "Wallet confirmation was cancelled.",
        detail: cleanedDetail,
      };
    }
    if (normalizedDetail.includes("insufficient") && normalizedDetail.includes("balance")) {
      return {
        message: "Insufficient balance for this route.",
        detail: cleanedDetail,
      };
    }
    if (
      normalizedDetail.includes("nonce too low") ||
      normalizedDetail.includes("nonce has already been used") ||
      normalizedDetail.includes("already known") ||
      normalizedDetail.includes("transaction underpriced") ||
      normalizedDetail.includes("replacement transaction underpriced")
    ) {
      return {
        message: "Your wallet has a pending transaction conflict.",
        detail: cleanedDetail || "Confirm or clear the older pending wallet transaction, then try the bridge again.",
      };
    }
    if (statusCode === 429) {
      return {
        message: "Live updates are taking a little longer than usual.",
        detail: cleanedDetail,
      };
    }
    if (statusCode === 503 && error?.response?.data?.error === "Bridge temporarily unavailable") {
      return {
        message: "Bridge temporarily unavailable — insufficient liquidity use another route please.",
        detail: cleanedDetail,
      };
    }
    if (statusCode >= 500) {
      return {
        message: "Bridge updates are temporarily unavailable.",
        detail: cleanedDetail,
      };
    }
    if (normalizedDetail.includes("switch") || normalizedDetail.includes("wrong chain") || normalizedDetail.includes("unsupported chain") || normalizedDetail.includes("network mismatch") || normalizedDetail.includes("please switch")) {
      return {
        message: "Switch your wallet to the required source network.",
        detail: cleanedDetail,
      };
    }
    return {
      message: fallbackMessage,
      detail: cleanedDetail,
    };
  }, []);

  const isNonceConflictError = useCallback((error) => {
    const rawDetail =
      typeof error === "string"
        ? error
        : error?.response?.data?.details ?? error?.response?.data?.message ?? error?.message ?? "";
    const normalizedDetail = rawDetail?.toString?.().toLowerCase?.() ?? "";
    return (
      normalizedDetail.includes("nonce too low") ||
      normalizedDetail.includes("nonce has already been used") ||
      normalizedDetail.includes("already known") ||
      normalizedDetail.includes("transaction underpriced") ||
      normalizedDetail.includes("replacement transaction underpriced")
    );
  }, []);

  const showFriendlyError = useCallback(
    (error, fallbackMessage) => {
      const friendly = getFriendlyError(error, fallbackMessage);
      showStatus(friendly.message, { tone: "error", detail: friendly.detail });
      showProgressError(friendly.message, friendly.detail);
      toast.error(friendly.message);
    },
    [getFriendlyError, showProgressError, showStatus]
  );

  const { connect, error: connectError } = useConnect();
  const { disconnect } = useDisconnect();
  const { address, isConnected, chainId } = useAccount();
  const { switchChainAsync } = useSwitchChain();
  const { writeContractAsync, error: writeError } = useWriteContract();
  const { sendTransactionAsync } = useSendTransaction();

  const routeConfig = getRouteConfig(tokenKey, direction, allTokens) || getRouteConfig(tokenKey, allTokens[tokenKey]?.routes[0], allTokens);
  const selectedToken = routeConfig?.token;
  const sourceChain = routeConfig?.sourceChain || "ETH";
  const destChain = routeConfig?.destChain || "BSC";
  const sourceAsset = routeConfig?.sourceAsset;
  const destAsset = routeConfig?.destAsset;
  const sourceLabel = routeConfig?.sourceLabel || CHAIN_LABELS[sourceChain];
  const destLabel = routeConfig?.destLabel || CHAIN_LABELS[destChain];
  const isXrpSource = sourceAsset?.mode === "xrp-native";
  const isXrpDestination = destAsset?.mode === "xrp-native";
  const xrpBridgeWalletAddress = sourceAsset?.bridgeWalletAddress || "";
  const bescDestForXrp = bescDestOverride;

  const isSolSource = sourceAsset?.mode === "sol-native";
  const isSolTokenSource = sourceAsset?.mode === "sol-token"; // WBESC SPL burn direction (SOL→EVM)
  const isSolSplSendSource = sourceAsset?.mode === "sol-spl-send"; // custom SOL SPL token → BESC
  const isSolSplSendDest = destAsset?.mode === "sol-spl-send";    // BESC → custom SOL SPL token
  const isSolDestination = destAsset?.mode === "sol-native" || destAsset?.mode === "sol-token" || isSolSplSendDest;
  const isSolTokenDestination = destAsset?.mode === "sol-token";
  const solBridgeWalletAddress = sourceAsset?.bridgeWalletAddress || "";
  const bescDestForSol = bescDestSolOverride;

  const isExternalChainSource = isXrpSource || isSolSource || isSolTokenSource || isSolSplSendSource;
  const currentChainId = isExternalChainSource ? undefined : CHAIN_IDS[sourceChain];
  const balanceTokenAddress = (sourceAsset?.mode === "token" || sourceAsset?.mode === "erc20-send") ? sourceAsset.address : undefined;
  const availableRoutes = selectedToken?.routes || EMPTY_ROUTE_LIST;
  const availableFromChains = Array.from(new Set(availableRoutes.map((route) => getRouteParts(route).sourceChain)));
  const availableToChains = Array.from(
    new Set(
      availableRoutes
        .filter((route) => getRouteParts(route).sourceChain === sourceChain)
        .map((route) => getRouteParts(route).destChain)
    )
  );
  const reverseRoute = availableRoutes.find((route) => {
    const parts = getRouteParts(route);
    return parts.sourceChain === destChain && parts.destChain === sourceChain;
  });

  const xrpWallet  = useXrpWallet({ enabled: isXrpSource });
  const wcXrp = useWalletConnectXrp({ enabled: isXrpSource });
  const solWallet  = useSolWallet({
    enabled: isSolSource || isSolTokenSource || isSolSplSendSource,
    splMintAddress: (isSolTokenSource || isSolSplSendSource) ? sourceAsset?.mintAddress : null,
    splDecimals: sourceAsset?.decimals ?? 9,
  });

  const { data: balance, error: balanceError } = useBalance({
    address,
    token: balanceTokenAddress,
    chainId: currentChainId,
    query: { enabled: !isExternalChainSource && !!address },
  });

  const isInteractionLocked = isLoading || Boolean(txHash) || bridgeProgress.open || Boolean(successState);
  const balanceText = isXrpSource
    ? (xrpWallet.connected ? (xrpWallet.formattedBalance !== null ? `${xrpWallet.formattedBalance} XRP` : "Loading...") : "— XRP")
    : isSolTokenSource
      ? (solWallet.connected ? (solWallet.splFormattedBalance !== null ? `${solWallet.splFormattedBalance} WBESC` : "Loading...") : "— WBESC")
      : isSolSplSendSource
        ? (solWallet.connected ? (solWallet.splFormattedBalance !== null ? `${solWallet.splFormattedBalance} ${sourceAsset?.symbol || ""}` : "Loading...") : `— ${sourceAsset?.symbol || ""}`)
        : isSolSource
          ? (solWallet.connected ? (solWallet.formattedBalance !== null ? `${solWallet.formattedBalance} SOL` : "Loading...") : "— SOL")
          : (balance && sourceAsset ? `${formatDisplayUnits(balance.value, sourceAsset.decimals)} ${sourceAsset.symbol}` : sourceAsset ? `0 ${sourceAsset.symbol}` : "0");
  const receiveEstimate = netAmount || "Calculating";

  useEffect(() => {
    const handlePointerDown = (event) => {
      if (selectorRegionRef.current && !selectorRegionRef.current.contains(event.target)) {
        setActiveSelector(null);
      }
    };

    document.addEventListener("mousedown", handlePointerDown);
    return () => document.removeEventListener("mousedown", handlePointerDown);
  }, []);

  useEffect(() => {
    const shouldLockScroll = showBridgeConfirm || bridgeProgress.open || Boolean(successState);
    const previousOverflow = document.body.style.overflow;

    if (shouldLockScroll) {
      document.body.style.overflow = "hidden";
    }

    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [showBridgeConfirm, bridgeProgress.open, successState]);

  useEffect(() => {
    const tokenDefinition = allTokens[tokenKey];
    if (tokenDefinition && !tokenDefinition.routes.includes(direction)) {
      setDirection(tokenDefinition.routes[0]);
    }
  }, [tokenKey, direction, allTokens]);

  useEffect(() => {
    if (!availableToChains.includes(destChain)) {
      const nextRoute = availableRoutes.find((route) => getRouteParts(route).sourceChain === sourceChain);
      if (nextRoute && nextRoute !== direction) {
        setDirection(nextRoute);
      }
    }
  }, [availableToChains, availableRoutes, destChain, direction, sourceChain]);

  // Auto-complete Xaman PKCE connection when returning from Xaman redirect.
  //
  // The PKCE exchange triggers two page loads:
  //   Load 2: URL has ?code=...  — connectXaman() exchanges code, then does a URL
  //           cleanup redirect before the promise resolves. We move state to
  //           xaman_pending so it survives into Load 3.
  //   Load 3: clean URL — XummPkce resolves instantly from its localStorage cache.
  useEffect(() => {
    const restoreRaw = sessionStorage.getItem('xaman_restore');
    const pendingRaw = sessionStorage.getItem('xaman_pending');

    if (restoreRaw) {
      try {
        const parsed = JSON.parse(restoreRaw);
        const attempts = (parsed.attempts || 0) + 1;
        sessionStorage.removeItem('xaman_restore');
        if (attempts <= 3) {
          sessionStorage.setItem('xaman_pending', JSON.stringify({ ...parsed, attempts }));
          xrpWallet.connectXaman().catch(() => {});
        }
      } catch {
        sessionStorage.removeItem('xaman_restore');
      }
      return;
    }

    if (pendingRaw) {
      sessionStorage.removeItem('xaman_pending');
      xrpWallet.connectXaman()
        .then(() => toast.success("Xaman connected."))
        .catch(() => {});
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Notify user when a Xaman signing request is ready; auto-open on mobile
  useEffect(() => {
    if (!xamanSigningUrl) return;
    const isMobile = /Mobi|Android|iPhone|iPad/i.test(navigator.userAgent);
    if (isMobile) {
      // Give framer-motion time to render the link before clicking it
      setTimeout(() => xamanLinkRef.current?.click(), 400);
    } else {
      toast("Open Xaman on your phone to approve the transaction.", { duration: 10_000 });
    }
  }, [xamanSigningUrl]);

  useEffect(() => {
    if (!hasMountedRef.current) {
      hasMountedRef.current = true;
      return;
    }

    setAmount("");
    setNetAmount("");
    clearStatus();
    setTxHash("");
    setAmountError("");
    setIsLoading(false);
    setBridgeProgress(createProgressState());
    setSuccessState(null);
    setBridgeContext(null);
    setActiveSelector(null);
    setXrpDestAddress("");
    setXrpDestError("");
    setBescDestOverride("");
    setSolDestAddress("");
    setSolDestError("");
    setBescDestSolOverride("");
  }, [tokenKey, direction, clearStatus]);

  useEffect(() => {
    if (!amount || !sourceAsset) {
      setAmountError("");
      return;
    }

    try {
      const parsedAmount = parseFloat(amount);
      if (Number.isNaN(parsedAmount)) {
        setAmountError("Enter a valid number.");
        return;
      }
      if (parsedAmount <= 0) {
        setAmountError("Amount must be greater than zero.");
        return;
      }

      if (isXrpSource) {
        const drops = parseUnits(amount.toString(), 6);
        if (drops < XRP_MIN_DROPS) {
          setAmountError("Minimum bridge amount is 10 XRP.");
          return;
        }
        if (xrpWallet.balanceDrops !== null && drops > xrpWallet.balanceDrops) {
          setAmountError(`Available: ${xrpWallet.formattedBalance} XRP`);
          return;
        }
        setAmountError("");
        return;
      }

      if (isSolSource) {
        const lamports = parseUnits(amount.toString(), 9);
        if (lamports < SOL_MIN_LAMPORTS) {
          setAmountError("Minimum bridge amount is 0.05 SOL.");
          return;
        }
        if (solWallet.balanceLamports !== null && lamports > solWallet.balanceLamports) {
          setAmountError(`Available: ${solWallet.formattedBalance} SOL`);
          return;
        }
        setAmountError("");
        return;
      }

      if (isSolTokenSource || isSolSplSendSource) {
        const decimals = sourceAsset?.decimals ?? 9;
        const symbol = sourceAsset?.symbol || "token";
        const raw = parseUnits(amount.toString(), decimals);
        if (solWallet.splBalanceSmallest !== null && raw > solWallet.splBalanceSmallest) {
          setAmountError(`Available: ${solWallet.splFormattedBalance} ${symbol}`);
          return;
        }
        setAmountError("");
        return;
      }

      if (!balance) {
        setAmountError("");
        return;
      }
      if (parseUnits(amount.toString(), sourceAsset.decimals) > balance.value) {
        setAmountError(`Available: ${formatDisplayUnits(balance.value, sourceAsset.decimals)} ${sourceAsset.symbol}`);
        return;
      }
      setAmountError("");
    } catch {
      setAmountError("Enter a valid amount for this token.");
    }
  }, [amount, balance, sourceAsset, isXrpSource, xrpWallet.balanceDrops, xrpWallet.formattedBalance, isSolSource, isSolTokenSource, isSolSplSendSource, solWallet.balanceLamports, solWallet.formattedBalance, solWallet.splBalanceSmallest, solWallet.splFormattedBalance]);

  useEffect(() => {
    if (amount && destAsset && !Number.isNaN(parseFloat(amount)) && parseFloat(amount) > 0 && !amountError) {
      try {
        const quote = getBridgeQuote(amount, sourceAsset, destAsset, tokenKey, allTokens[tokenKey]);
        setNetAmount(quote?.formattedReceive || "");
        setBridgeQuote(quote || null);
        return;
      } catch {
        setNetAmount("");
        setBridgeQuote(null);
        return;
      }
    }
    setNetAmount("");
    setBridgeQuote(null);
  }, [amount, sourceAsset, destAsset, amountError, tokenKey]);

  useEffect(() => {
    if (!txHash || !destAsset || !bridgeContext) {
      return undefined;
    }

    const interval = setInterval(async () => {
      try {
        const response = await axios({
          method: "get",
          url: `${process.env.REACT_APP_BACKEND_URL}/transaction/${txHash}`,
          headers: { "x-api-key": process.env.REACT_APP_API_KEY },
        });
        const { status: txStatus, amount: bridgedAmount, fee, destinationTxHash } = response.data;

        if (txStatus === "completed") {
          const displayAmountRaw = bridgedAmount ? scaleAmount(bridgedAmount, bridgeContext.sourceDecimals, destAsset.decimals) : null;
          const displayFeeRaw = fee ? scaleAmount(fee, bridgeContext.sourceDecimals, destAsset.decimals) : null;
          const formattedAmount = displayAmountRaw ? formatDisplayUnits(displayAmountRaw, destAsset.decimals) : bridgeContext.estimatedReceive || netAmount || "0";
          const feeText = displayFeeRaw ? `${formatDisplayUnits(displayFeeRaw, destAsset.decimals)} ${destAsset.symbol} bridge fee applied.` : "";

          showSuccessStatus(`Bridge complete. ${formattedAmount} ${destAsset.symbol} is ready on ${destLabel}.`, feeText);
          setProgressStep("delivered", "Delivered on destination chain", `${formattedAmount} ${destAsset.symbol} is now available on ${destLabel}.`, {
            tone: "success",
            sourceTxHash: txHash,
            destinationTxHash: destinationTxHash || "",
          });

          const finalExplorerLink = getExplorerLink(destChain, destinationTxHash) || getExplorerLink(sourceChain, txHash);
          window.setTimeout(() => {
            setBridgeProgress(createProgressState());
            setSuccessState({
              amountSent: bridgeContext.amountSent,
              amountReceived: formattedAmount,
              sourceSymbol: bridgeContext.sourceSymbol,
              destinationSymbol: destAsset.symbol,
              destinationLabel: destLabel,
              sourceTxHash: txHash,
              destinationTxHash: destinationTxHash || "",
              explorerLink: finalExplorerLink,
            });
          }, 700);

          toast.success("Bridge transfer delivered.");
          setAmount("");
          setNetAmount("");
          setAmountError("");
          setTxHash("");
          setBridgeContext(null);
          setIsLoading(false);
          clearInterval(interval);
          return;
        }

        if (txStatus === "failed") {
          showErrorStatus(
            `Bridge failed before the ${destAsset.symbol} release completed.`,
            `Your transfer was received, but delivery on ${destLabel} has not finished yet.`
          );
          showProgressError(
            "Transfer needs attention",
            `Your transfer is taking longer than expected to arrive on ${destLabel}.`
          );
          setTxHash("");
          setIsLoading(false);
          clearInterval(interval);
          toast.error("Bridge delivery did not finalize.");
          return;
        }

        setProgressStep(
          "processing",
          "Processing bridge transfer",
          `Source tx ${shortValue(txHash)} is confirmed. Destination delivery on ${destLabel} is still being processed.`,
          {
            sourceTxHash: txHash,
            destinationTxHash: destinationTxHash || "",
          }
        );
        showInfoStatus(
          `Bridge in progress on ${destLabel}.`,
          `Source tx ${shortValue(txHash)} is confirmed. You can leave this page; delivery takes a few minutes.`
        );
      } catch (error) {
        if (error.response?.status === 429) {
          showInfoStatus("Live updates are taking a little longer than usual.", "We will keep checking automatically.");
          return;
        }
        if (error.response?.status === 404 || error.response?.status === 400) {
          // 404 = tx not in DB yet (scanner hasn't picked it up)
          // 400 = hash format unrecognised (e.g. XRP hash before backend update) — same treatment
          setProgressStep(
            "processing",
            "Finalizing your transfer",
            `Your transfer has been sent and is being prepared for delivery on ${destLabel}.`,
            { sourceTxHash: txHash }
          );
          showInfoStatus(
            "Preparing your transfer.",
            `Your transfer has been sent. You can leave this page and check back for updates.`
          );
          return;
        }

        showFriendlyError(error, "Unable to read bridge status right now.");
        setTxHash("");
        setIsLoading(false);
        clearInterval(interval);
      }
    }, 5000);

    return () => clearInterval(interval);
  }, [
    txHash,
    destAsset,
    destChain,
    destLabel,
    netAmount,
    bridgeContext,
    scaleAmount,
    showErrorStatus,
    showFriendlyError,
    showInfoStatus,
    showProgressError,
    showSuccessStatus,
    sourceChain,
    setProgressStep,
  ]);

  useEffect(() => {
    if (connectError) {
      showFriendlyError(connectError, "Unable to connect to your wallet.");
    }
    if (balanceError) {
      showFriendlyError(balanceError, "Unable to fetch your balance.");
    }
    if (writeError) {
      let errorMessage = writeError.message || "Unknown error";
      if (writeError.data && sourceAsset?.abi) {
        try {
          const decodedError = decodeErrorResult({
            abi: sourceAsset.abi,
            data: writeError.data,
          });
          errorMessage = decodedError.errorName || decodedError.args?.toString() || errorMessage;
        } catch (decodeError) {
          console.error("Failed to decode contract error:", decodeError);
        }
      }
      const friendly = getFriendlyError({ ...writeError, message: errorMessage }, "Wallet transaction failed.");
      showErrorStatus(friendly.message, friendly.detail || errorMessage);
      showProgressError(friendly.message, friendly.detail || errorMessage);
      setIsLoading(false);
      bridgeSubmitLockRef.current = false;
    }
  }, [connectError, balanceError, writeError, sourceAsset, getFriendlyError, showErrorStatus, showFriendlyError, showProgressError]);

  useEffect(() => {
    if (!isConnected && !isExternalChainSource && (isLoading || txHash)) {
      showInfoStatus("Wallet disconnected.", "Reconnect your wallet to continue.");
      setIsLoading(false);
      setTxHash("");
      setAmount("");
      setNetAmount("");
      setBridgeProgress(createProgressState());
      setBridgeContext(null);
    }
  }, [isConnected, isExternalChainSource, isLoading, txHash, showInfoStatus]);

  const handleSwitchChain = useCallback(async () => {
    if (chainId === currentChainId) {
      clearStatus();
      return;
    }

    showInfoStatus("Switching wallet network...", `Required source: ${sourceLabel}.`);
    try {
      await switchChainAsync({ chainId: currentChainId });
      showSuccessStatus(`Switched to ${sourceLabel}.`);
      toast.success(`Wallet switched to ${sourceLabel}.`);
    } catch (error) {
      showFriendlyError(error, "Unable to switch networks automatically.");
    }
  }, [chainId, currentChainId, sourceLabel, switchChainAsync, clearStatus, showFriendlyError, showInfoStatus, showSuccessStatus]);

  const submitPrecheck = useCallback(
    (amountParsed) =>
      axios({
        method: "post",
        url: `${process.env.REACT_APP_BACKEND_URL}/precheck`,
        data: {
          user: address,
          amount: amountParsed.toString(),
          direction,
          token: selectedToken.apiToken,
        },
        headers: { "x-api-key": process.env.REACT_APP_API_KEY },
      }),
    [address, direction, selectedToken]
  );

  const submitBridgeRequest = useCallback(
    async (endpoint, tx, amountParsed, newIdempotencyKey, extraData = {}) => {
      for (let attempt = 1; attempt <= MAX_RETRIES; attempt += 1) {
        try {
          const response = await axios({
            method: "post",
            url: `${process.env.REACT_APP_BACKEND_URL}/${endpoint}`,
            data: {
              user: address,
              amount: amountParsed.toString(),
              txHash: tx,
              idempotencyKey: newIdempotencyKey,
              token: selectedToken.apiToken,
              direction,
              ...extraData,
            },
            headers: { "x-api-key": process.env.REACT_APP_API_KEY },
          });
          if (response.data.error) {
            throw new Error(response.data.details || response.data.error);
          }
          return response;
        } catch (error) {
          if (
            error.response?.status === 400 &&
            error.response?.data?.details?.includes("No transaction data") &&
            attempt < MAX_RETRIES
          ) {
            setProgressStep(
              "processing",
              "Completing your transfer",
              `Your transfer is being prepared for the next step. Retry ${attempt} of ${MAX_RETRIES}.`
            );
            showInfoStatus("Still working on your transfer.", `Retry ${attempt} of ${MAX_RETRIES}.`);
            await new Promise((resolve) => setTimeout(resolve, RETRY_DELAY * Math.pow(2, attempt)));
            continue;
          }
          throw error;
        }
      }
      return null;
    },
    [address, direction, selectedToken, setProgressStep, showInfoStatus]
  );

  // XRP destination validation
  useEffect(() => {
    if (!isXrpDestination || !xrpDestAddress) { setXrpDestError(""); return; }
    setXrpDestError(XRP_RADDRESS_REGEX.test(xrpDestAddress) ? "" : "Enter a valid XRP r-address (starts with r).");
  }, [isXrpDestination, xrpDestAddress]);

  // SOL destination validation
  useEffect(() => {
    if (!isSolDestination || !solDestAddress) { setSolDestError(""); return; }
    setSolDestError(SOL_ADDRESS_REGEX.test(solDestAddress) ? "" : "Enter a valid Solana address.");
  }, [isSolDestination, solDestAddress]);

  const handleBridge = useCallback(async () => {
    if (bridgeSubmitLockRef.current) {
      return;
    }
    bridgeSubmitLockRef.current = true;

    if (!routeConfig || !selectedToken || !sourceAsset || !destAsset) {
      showErrorStatus("This bridge route is not configured yet.");
      bridgeSubmitLockRef.current = false;
      return;
    }
    if (isXrpSource) {
      // ── XRP → BESC path ──────────────────────────────────────────────────
      if (!xrpWallet.connected) {
        showInfoStatus("Connect GemWallet or Xaman to start a bridge.");
        bridgeSubmitLockRef.current = false;
        return;
      }
      if (!bescDestForXrp || !/^0x[0-9a-fA-F]{40}$/.test(bescDestForXrp)) {
        showInfoStatus("Enter a valid BESC destination address.");
        bridgeSubmitLockRef.current = false;
        return;
      }
      if (!amount || Number.isNaN(parseFloat(amount)) || parseFloat(amount) <= 0) {
        showInfoStatus("Enter an amount greater than zero.");
        bridgeSubmitLockRef.current = false;
        return;
      }

      let amountParsed;
      try { amountParsed = parseUnits(amount.toString(), 6); } catch {
        showErrorStatus("Amount format is invalid.");
        bridgeSubmitLockRef.current = false;
        return;
      }

      if (amountParsed < XRP_MIN_DROPS) {
        showErrorStatus("Minimum bridge amount is 10 XRP.");
        bridgeSubmitLockRef.current = false;
        return;
      }

      if (xrpWallet.balanceDrops !== null && amountParsed > xrpWallet.balanceDrops) {
        showErrorStatus(`Insufficient XRP balance.`, xrpWallet.formattedBalance ? `Available: ${xrpWallet.formattedBalance} XRP` : "");
        bridgeSubmitLockRef.current = false;
        return;
      }

      setIsLoading(true);
      setSuccessState(null);
      setBridgeProgress(createProgressState());
      setProgressStep("signature", "Approve in wallet", `Approve the XRP transfer in your ${xrpWallet.walletType === "xaman" ? "Xaman app" : "GemWallet"}.`);

      try {
        let tx;
        try {
          const walletLabel = wcXrp.connected ? "WalletConnect"
            : xrpWallet.walletType === "xaman" ? "Xaman app"
            : xrpWallet.walletType === "bescwallet" ? "BESC Wallet"
            : "GemWallet";
          showInfoStatus(
            `Confirm the XRP payment in your ${walletLabel}.`,
            `Sending ${amount} XRP to the bridge. Destination BESC: ${bescDestForXrp.slice(0, 6)}...${bescDestForXrp.slice(-4)}`
          );
          tx = wcXrp.connected
            ? await wcXrp.send({ drops: amountParsed, destination: xrpBridgeWalletAddress, bescAddress: bescDestForXrp })
            : await xrpWallet.send({
                drops: amountParsed,
                destination: xrpBridgeWalletAddress,
                bescAddress: bescDestForXrp,
                onPayloadReady: (url) => setXamanSigningUrl(url || null),
              });
          setXamanSigningUrl(null);
        } catch (error) {
          setXamanSigningUrl(null);
          const friendly = getFriendlyError(error, "XRP payment could not be submitted.");
          if (error?.message?.includes("rejected") || error?.message?.includes("cancelled")) {
            showInfoStatus("Bridge cancelled before submission.");
            setBridgeProgress(createProgressState());
            setIsLoading(false);
            toast("Bridge cancelled.");
            return;
          }
          throw new Error(friendly.detail || friendly.message);
        }

        setProgressStep("submitted", "XRP payment submitted", `XRPL transaction ${shortValue(tx)} has been broadcast.`, { sourceTxHash: tx });
        toast.success("XRP payment submitted.");
        showInfoStatus(`Waiting for XRP confirmation on XRP Ledger.`, `Source tx ${shortValue(tx)}.`);
        setProgressStep("confirming", "Waiting for XRPL confirmation", "Your XRP payment is confirming on the XRP Ledger.", { sourceTxHash: tx });

        setTxHash(tx);
        setBridgeContext({
          amountSent: formatDisplayNumber(amount, 6),
          estimatedReceive: netAmount,
          sourceSymbol: sourceAsset.symbol,
          destinationSymbol: destAsset.symbol,
          sourceDecimals: 6,
        });
        setProgressStep("processing", "Minting WXRP on BESC", "XRP confirmed. Minting WXRP on BESC HyperChain.", { sourceTxHash: tx });
        showInfoStatus("XRP confirmed.", `Minting ${destAsset.symbol} on ${destLabel}.`);
        setIsLoading(false);
      } catch (error) {
        showFriendlyError(error, "XRP bridge submission failed.");
        setTxHash("");
        setIsLoading(false);
      } finally {
        bridgeSubmitLockRef.current = false;
      }
      return;
    }

    if (isSolSource) {
      // ── SOL → BESC path ──────────────────────────────────────────────────
      if (!solWallet.connected) {
        showInfoStatus("Connect Phantom or Solflare to start a bridge.");
        bridgeSubmitLockRef.current = false;
        return;
      }
      if (!bescDestForSol || !/^0x[0-9a-fA-F]{40}$/.test(bescDestForSol)) {
        showInfoStatus("Enter a valid BESC destination address.");
        bridgeSubmitLockRef.current = false;
        return;
      }
      if (!amount || Number.isNaN(parseFloat(amount)) || parseFloat(amount) <= 0) {
        showInfoStatus("Enter an amount greater than zero.");
        bridgeSubmitLockRef.current = false;
        return;
      }

      let amountParsed;
      try { amountParsed = parseUnits(amount.toString(), 9); } catch {
        showErrorStatus("Amount format is invalid.");
        bridgeSubmitLockRef.current = false;
        return;
      }

      if (amountParsed < SOL_MIN_LAMPORTS) {
        showErrorStatus("Minimum bridge amount is 0.05 SOL.");
        bridgeSubmitLockRef.current = false;
        return;
      }

      if (solWallet.balanceLamports !== null && amountParsed > solWallet.balanceLamports) {
        showErrorStatus(`Insufficient SOL balance.`, solWallet.formattedBalance ? `Available: ${solWallet.formattedBalance} SOL` : "");
        bridgeSubmitLockRef.current = false;
        return;
      }

      setIsLoading(true);
      setSuccessState(null);
      setBridgeProgress(createProgressState());
      setProgressStep("signature", "Approve in wallet", `Approve the SOL transfer in your ${solWallet.walletType === "solflare" ? "Solflare" : "Phantom"} wallet.`);

      try {
        let tx;
        try {
          showInfoStatus(
            `Confirm the SOL payment in your ${solWallet.walletType === "solflare" ? "Solflare" : "Phantom"} wallet.`,
            `Sending ${amount} SOL to the bridge. Destination BESC: ${bescDestForSol.slice(0, 6)}...${bescDestForSol.slice(-4)}`
          );
          tx = await solWallet.send({
            lamports: amountParsed,
            destination: solBridgeWalletAddress,
            bescAddress: bescDestForSol,
          });
        } catch (error) {
          const friendly = getFriendlyError(error, "SOL payment could not be submitted.");
          if (error?.message?.includes("rejected") || error?.message?.includes("cancelled") || error?.message?.includes("User rejected")) {
            showInfoStatus("Bridge cancelled before submission.");
            setBridgeProgress(createProgressState());
            setIsLoading(false);
            toast("Bridge cancelled.");
            return;
          }
          throw new Error(friendly.detail || friendly.message);
        }

        setProgressStep("submitted", "SOL payment submitted", `Solana transaction ${shortValue(tx)} has been broadcast.`, { sourceTxHash: tx });
        toast.success("SOL payment submitted.");
        showInfoStatus(`Waiting for SOL confirmation on Solana.`, `Source tx ${shortValue(tx)}.`);
        setProgressStep("confirming", "Waiting for Solana confirmation", "Your SOL payment is confirming on Solana.", { sourceTxHash: tx });

        setTxHash(tx);
        setBridgeContext({
          amountSent: formatDisplayNumber(amount, 6),
          estimatedReceive: netAmount,
          sourceSymbol: sourceAsset.symbol,
          destinationSymbol: destAsset.symbol,
          sourceDecimals: 9,
        });
        setProgressStep("processing", "Minting WSOL on BESC", "SOL confirmed. Minting WSOL on BESC HyperChain.", { sourceTxHash: tx });
        showInfoStatus("SOL confirmed.", `Minting ${destAsset.symbol} on ${destLabel}.`);
        setIsLoading(false);
      } catch (error) {
        showFriendlyError(error, "SOL bridge submission failed.");
        setTxHash("");
        setIsLoading(false);
      } finally {
        bridgeSubmitLockRef.current = false;
      }
      return;
    }

    if (isSolTokenSource) {
      // ── WBESC SOL → EVM path: burn SPL token, scanner picks up the burn ────
      if (!solWallet.connected) {
        showInfoStatus("Connect Phantom or Solflare to start a bridge.");
        bridgeSubmitLockRef.current = false;
        return;
      }
      if (!bescDestForSol || !/^0x[0-9a-fA-F]{40}$/.test(bescDestForSol)) {
        showInfoStatus("Enter a valid destination address for the selected chain.");
        bridgeSubmitLockRef.current = false;
        return;
      }
      if (!amount || Number.isNaN(parseFloat(amount)) || parseFloat(amount) <= 0) {
        showInfoStatus("Enter an amount greater than zero.");
        bridgeSubmitLockRef.current = false;
        return;
      }

      let amountParsed;
      try { amountParsed = parseUnits(amount.toString(), 9); } catch {
        showErrorStatus("Amount format is invalid.");
        bridgeSubmitLockRef.current = false;
        return;
      }

      if (amountParsed < 1_000_000n) {
        showErrorStatus("Minimum bridge amount is 0.001 WBESC.");
        bridgeSubmitLockRef.current = false;
        return;
      }

      if (solWallet.splBalanceSmallest !== null && amountParsed > solWallet.splBalanceSmallest) {
        showErrorStatus("Insufficient WBESC balance.", solWallet.splFormattedBalance ? `Available: ${solWallet.splFormattedBalance} WBESC` : "");
        bridgeSubmitLockRef.current = false;
        return;
      }

      if (!sourceAsset?.mintAddress) {
        showErrorStatus("WBESC SPL mint address is not configured.");
        bridgeSubmitLockRef.current = false;
        return;
      }

      setIsLoading(true);
      setSuccessState(null);
      setBridgeProgress(createProgressState());
      setProgressStep("signature", "Approve in wallet", `Approve the WBESC burn in your ${solWallet.walletType === "solflare" ? "Solflare" : "Phantom"} wallet.`);

      try {
        let tx;
        try {
          showInfoStatus(
            `Confirm the WBESC burn in your ${solWallet.walletType === "solflare" ? "Solflare" : "Phantom"} wallet.`,
            `Burning ${amount} WBESC → ${destChain}:${bescDestForSol.slice(0, 6)}...${bescDestForSol.slice(-4)}`
          );
          tx = await solWallet.burnSplToken({
            mintAddress: sourceAsset.mintAddress,
            decimals: 9,
            amount: amountParsed,
            destinationChain: destChain,
            evmAddress: bescDestForSol,
          });
        } catch (error) {
          if (error?.message?.includes("rejected") || error?.message?.includes("cancelled") || error?.message?.includes("User rejected")) {
            showInfoStatus("Bridge cancelled before submission.");
            setBridgeProgress(createProgressState());
            setIsLoading(false);
            toast("Bridge cancelled.");
            return;
          }
          throw new Error(getFriendlyError(error, "WBESC burn could not be submitted.").detail || error.message);
        }

        const txKey = tx.toLowerCase(); // normalize before storing — prevents case-variant replay
        setProgressStep("submitted", "WBESC burn submitted", `Solana tx ${shortValue(txKey)} broadcast.`, { sourceTxHash: txKey });
        toast.success("WBESC burn submitted.");
        setProgressStep("confirming", "Waiting for Solana confirmation", "Your WBESC burn is confirming on Solana.", { sourceTxHash: txKey });

        setTxHash(txKey);
        setBridgeContext({
          amountSent: formatDisplayNumber(amount, 6),
          estimatedReceive: netAmount,
          sourceSymbol: sourceAsset.symbol,
          destinationSymbol: destAsset.symbol,
          sourceDecimals: 9,
        });
        setProgressStep("processing", `Releasing ${destAsset.symbol} on ${destLabel}`, `Burn confirmed. Releasing ${destAsset.symbol} on ${destLabel}.`, { sourceTxHash: txKey });
        showInfoStatus("WBESC burn confirmed.", `Releasing ${destAsset.symbol} on ${destLabel}.`);
        setIsLoading(false);
      } catch (error) {
        showFriendlyError(error, "WBESC SOL bridge failed.");
        setTxHash("");
        setIsLoading(false);
      } finally {
        bridgeSubmitLockRef.current = false;
      }
      return;
    }

    if (isSolSplSendSource) {
      // ── Custom SOL SPL token → BESC path ────────────────────────────────────
      // User transfers SPL tokens to the bridge's Solana wallet; scanner mints
      // the wrapped token on BESC once the transfer is detected.
      if (!solWallet.connected) {
        showInfoStatus("Connect Phantom or Solflare to start a bridge.");
        bridgeSubmitLockRef.current = false;
        return;
      }
      if (!bescDestForSol || !/^0x[0-9a-fA-F]{40}$/.test(bescDestForSol)) {
        showInfoStatus("Enter a valid BESC destination address.");
        bridgeSubmitLockRef.current = false;
        return;
      }
      if (!amount || Number.isNaN(parseFloat(amount)) || parseFloat(amount) <= 0) {
        showInfoStatus("Enter an amount greater than zero.");
        bridgeSubmitLockRef.current = false;
        return;
      }
      if (!sourceAsset?.mintAddress) {
        showErrorStatus("Token mint address is not configured.");
        bridgeSubmitLockRef.current = false;
        return;
      }
      if (!sourceAsset?.bridgeWalletAddress) {
        showErrorStatus("Bridge wallet address is not configured for this token.");
        bridgeSubmitLockRef.current = false;
        return;
      }

      const decimals = sourceAsset.decimals ?? 9;
      let amountParsed;
      try { amountParsed = parseUnits(amount.toString(), decimals); } catch {
        showErrorStatus("Amount format is invalid.");
        bridgeSubmitLockRef.current = false;
        return;
      }

      if (solWallet.splBalanceSmallest !== null && amountParsed > solWallet.splBalanceSmallest) {
        showErrorStatus(`Insufficient ${sourceAsset.symbol} balance.`, solWallet.splFormattedBalance ? `Available: ${solWallet.splFormattedBalance} ${sourceAsset.symbol}` : "");
        bridgeSubmitLockRef.current = false;
        return;
      }

      setIsLoading(true);
      setSuccessState(null);
      setBridgeProgress(createProgressState());
      const walletLabel = solWallet.walletType === "solflare" ? "Solflare" : "Phantom";
      setProgressStep("signature", "Approve in wallet", `Approve the ${sourceAsset.symbol} transfer in your ${walletLabel} wallet.`);

      try {
        let tx;
        try {
          showInfoStatus(
            `Confirm the ${sourceAsset.symbol} transfer in your ${walletLabel} wallet.`,
            `Sending to bridge. BESC destination: ${bescDestForSol.slice(0, 6)}...${bescDestForSol.slice(-4)}`
          );
          tx = await solWallet.sendSplToken({
            mintAddress: sourceAsset.mintAddress,
            decimals,
            amount: amountParsed,
            destination: sourceAsset.bridgeWalletAddress,
            bescAddress: bescDestForSol,
          });
        } catch (error) {
          if (error?.message?.includes("rejected") || error?.message?.includes("cancelled") || error?.message?.includes("User rejected")) {
            showInfoStatus("Bridge cancelled before submission.");
            setBridgeProgress(createProgressState());
            setIsLoading(false);
            toast("Bridge cancelled.");
            return;
          }
          throw new Error(getFriendlyError(error, `${sourceAsset.symbol} transfer could not be submitted.`).detail || error.message);
        }

        const txKey = tx.toLowerCase();
        setProgressStep("submitted", `${sourceAsset.symbol} transfer submitted`, `Solana tx ${shortValue(txKey)} broadcast.`, { sourceTxHash: txKey });
        toast.success("Transfer submitted.");
        showInfoStatus(`Waiting for ${sourceAsset.symbol} confirmation on Solana.`, `Source tx ${shortValue(txKey)}.`);
        setProgressStep("confirming", "Waiting for Solana confirmation", `Your ${sourceAsset.symbol} transfer is confirming on Solana.`, { sourceTxHash: txKey });

        setTxHash(txKey);
        setBridgeContext({
          amountSent: formatDisplayNumber(amount, Math.min(decimals, 6)),
          estimatedReceive: netAmount,
          sourceSymbol: sourceAsset.symbol,
          destinationSymbol: destAsset.symbol,
          sourceDecimals: decimals,
        });
        setProgressStep("processing", `Minting ${destAsset.symbol} on BESC`, `Transfer confirmed. Minting ${destAsset.symbol} on BESC HyperChain.`, { sourceTxHash: txKey });
        showInfoStatus(`${sourceAsset.symbol} transfer confirmed.`, `Minting ${destAsset.symbol} on BESC HyperChain.`);
        setIsLoading(false);
      } catch (error) {
        showFriendlyError(error, `${sourceAsset?.symbol || "Token"} bridge submission failed.`);
        setTxHash("");
        setIsLoading(false);
      } finally {
        bridgeSubmitLockRef.current = false;
      }
      return;
    }

    if (!isConnected) {
      showInfoStatus("Connect your wallet to start a bridge.");
      bridgeSubmitLockRef.current = false;
      return;
    }
    if (chainId !== currentChainId) {
      showInfoStatus(`Switch to ${sourceLabel} first.`);
      bridgeSubmitLockRef.current = false;
      return;
    }
    if (!amount || Number.isNaN(parseFloat(amount)) || parseFloat(amount) <= 0) {
      showInfoStatus("Enter an amount greater than zero.");
      bridgeSubmitLockRef.current = false;
      return;
    }

    // BESC → XRP: validate destination address
    if (isXrpDestination && (!xrpDestAddress || !XRP_RADDRESS_REGEX.test(xrpDestAddress))) {
      showErrorStatus("Enter a valid XRP destination address (r-address) before bridging.");
      bridgeSubmitLockRef.current = false;
      return;
    }

    // BESC → SOL: validate destination address
    if (isSolDestination && (!solDestAddress || !SOL_ADDRESS_REGEX.test(solDestAddress))) {
      showErrorStatus("Enter a valid Solana destination address before bridging.");
      bridgeSubmitLockRef.current = false;
      return;
    }

    let amountParsed;
    try {
      amountParsed = parseUnits(amount.toString(), sourceAsset.decimals);
    } catch {
      showErrorStatus("Amount format is invalid for this asset.");
      bridgeSubmitLockRef.current = false;
      return;
    }

    if (!balance || amountParsed > balance.value) {
      showErrorStatus(
        `Insufficient ${sourceAsset.symbol} balance.`,
        balance ? `Available: ${formatDisplayUnits(balance.value, sourceAsset.decimals)} ${sourceAsset.symbol}` : ""
      );
      bridgeSubmitLockRef.current = false;
      return;
    }

    setIsLoading(true);
    setSuccessState(null);
    setBridgeProgress(createProgressState());
    setProgressStep("signature", "Approve in wallet", `Approve the ${sourceAsset.symbol} transfer to start the bridge.`);
    const newIdempotencyKey = uuidv4();

    try {
      await submitPrecheck(amountParsed);
      const client = publicClients[sourceChain.toLowerCase()];
      let tx;

      if (sourceAsset.mode === "native") {
        if (!sourceAsset.receiver) {
          throw new Error("This native route is missing a bridge receiver address.");
        }
        showInfoStatus(`Confirm the ${sourceAsset.symbol} transfer in your wallet.`, `Bridge receiver: ${shortValue(sourceAsset.receiver)}.`);

        for (let attempt = 1; attempt <= MAX_RETRIES; attempt += 1) {
          try {
            tx = await sendTransactionAsync({
              to: sourceAsset.receiver,
              value: amountParsed,
              chainId: currentChainId,
              gas: sourceChain === "BESC" ? GAS_LIMIT : undefined,
            });
            break;
          } catch (error) {
            const friendly = getFriendlyError(error, "Native transfer could not be submitted.");
            if (error?.code === 4001 || friendly.message === "Wallet confirmation was cancelled.") {
              showInfoStatus("Bridge cancelled before submission.");
              setBridgeProgress(createProgressState());
              setIsLoading(false);
              toast("Bridge cancelled.");
              return;
            }
            if (isNonceConflictError(error)) {
              throw new Error(friendly.detail || friendly.message);
            }
            if (attempt === MAX_RETRIES) {
              throw new Error(friendly.detail || friendly.message);
            }
            showInfoStatus(`Transfer attempt ${attempt} failed. Retrying soon...`, friendly.detail);
            await new Promise((resolve) => setTimeout(resolve, RETRY_DELAY));
          }
        }
      } else {
        if (!sourceAsset.address) {
          throw new Error("This token route is missing a source contract address.");
        }
        if (sourceAsset.checks?.tradingOpen) {
          const tradingOpen = await client.readContract({
            address: sourceAsset.address,
            abi: sourceAsset.abi,
            functionName: "tradingOpen",
          });
          if (!tradingOpen && sourceAsset.checks.authorized) {
            const isAuthorized = await client.readContract({
              address: sourceAsset.address,
              abi: sourceAsset.abi,
              functionName: "isAuthorized",
              args: [address],
            });
            if (!isAuthorized) {
              throw new Error("Trading is not enabled and this wallet is not authorized.");
            }
          }
        }
        if (sourceAsset.checks?.blacklisted) {
          const isBlacklisted = await client.readContract({
            address: sourceAsset.address,
            abi: sourceAsset.abi,
            functionName: "isBlacklisted",
            args: [address],
          });
          if (isBlacklisted) {
            throw new Error("This wallet is blacklisted on the source token contract.");
          }
        }
        if (sourceAsset.checks?.paused) {
          const paused = await client.readContract({
            address: sourceAsset.address,
            abi: sourceAsset.abi,
            functionName: "paused",
          });
          if (paused) {
            throw new Error("The source token contract is paused.");
          }
        }

        const isErc20Send = sourceAsset.mode === "erc20-send";
        showInfoStatus(`Confirm the ${sourceAsset.symbol} ${isErc20Send ? "transfer" : "burn"} in your wallet.`);

        for (let attempt = 1; attempt <= MAX_RETRIES; attempt += 1) {
          try {
            tx = await writeContractAsync(
              isErc20Send
                ? {
                    // ERC20 transfer to bridge receiver (e.g. USDC → bridge wallet)
                    address: sourceAsset.address,
                    abi: sourceAsset.abi,
                    functionName: "transfer",
                    args: [sourceAsset.bridgeReceiverAddress, amountParsed],
                    chainId: currentChainId,
                    gas: GAS_LIMIT,
                  }
                : {
                    // Standard burn
                    address: sourceAsset.address,
                    abi: sourceAsset.abi,
                    functionName: "burn",
                    args: [amountParsed],
                    chainId: currentChainId,
                    gas: GAS_LIMIT,
                  }
            );
            break;
          } catch (error) {
            const friendly = getFriendlyError(error, isErc20Send ? "Transfer could not be submitted." : "Burn transaction could not be submitted.");
            if (error?.code === 4001 || friendly.message === "Wallet confirmation was cancelled.") {
              showInfoStatus("Bridge cancelled before submission.");
              setBridgeProgress(createProgressState());
              setIsLoading(false);
              toast("Bridge cancelled.");
              return;
            }
            if (isNonceConflictError(error)) {
              throw new Error(friendly.detail || friendly.message);
            }
            if (attempt === MAX_RETRIES) {
              throw new Error(friendly.detail || friendly.message);
            }
            showInfoStatus(`Attempt ${attempt} failed. Retrying soon...`, friendly.detail);
            await new Promise((resolve) => setTimeout(resolve, RETRY_DELAY));
          }
        }
      }

      setProgressStep("submitted", "Transaction submitted", `Source transaction ${shortValue(tx)} has been broadcast on ${sourceLabel}.`, {
        sourceTxHash: tx,
      });
      toast.success("Transaction submitted.");
      showInfoStatus(`Waiting for ${sourceAsset.symbol} confirmation on ${sourceLabel}.`, `Source tx ${shortValue(tx)}.`);

      setProgressStep("confirming", "Waiting for network confirmation", `Waiting for the transfer to confirm before final delivery begins.`, {
        sourceTxHash: tx,
      });

      try {
        await waitForTransactionReceipt(client, {
          hash: tx,
          confirmations: 1,
          timeout: SOURCE_RECEIPT_TIMEOUT_MS[sourceChain] || SOURCE_RECEIPT_TIMEOUT_MS.BSC,
          pollingInterval: SOURCE_RECEIPT_POLL_MS[sourceChain] || SOURCE_RECEIPT_POLL_MS.BSC,
        });
      } catch {
        // Frontend RPC couldn't confirm — tx is already on-chain, backend verifies independently
      }

      setTxHash(tx);
      setBridgeContext({
        amountSent: formatDisplayNumber(amount, Math.min(sourceAsset.decimals, 6)),
        estimatedReceive: netAmount,
        sourceSymbol: sourceAsset.symbol,
        destinationSymbol: destAsset.symbol,
        sourceDecimals: sourceAsset.decimals,
      });
      setProgressStep("processing", "Completing your transfer", `Your transfer is confirmed. Preparing delivery on ${destLabel}.`, {
        sourceTxHash: tx,
      });
      showInfoStatus("Transfer confirmed.", `Preparing ${destAsset.symbol} for delivery on ${destLabel}.`);

      const endpoint = destChain === "BESC" ? "redeem" : "initiate";
      const extraData = isXrpDestination ? { xrpDestination: xrpDestAddress } : isSolDestination ? { solDestination: solDestAddress } : {};
      // For reflection tokens, the Transfer event emits the post-tax amount.
      // The API's amount parameter must match the event amount, not the user's input.
      const sourceTaxBps = BigInt(sourceAsset.sourceTokenTaxBps || 0);
      const apiAmountParsed = sourceTaxBps > 0n
        ? amountParsed - (amountParsed * sourceTaxBps) / 10000n
        : amountParsed;
      await submitBridgeRequest(endpoint, tx, apiAmountParsed, newIdempotencyKey, extraData);

      showInfoStatus(`Bridge submitted. Waiting for ${destAsset.symbol} on ${destLabel}.`, `Source tx ${shortValue(tx)} is confirmed.`);
      setIsLoading(false);
    } catch (error) {
      showFriendlyError(error, "Bridge submission failed.");
      setTxHash("");
      setIsLoading(false);
    } finally {
      bridgeSubmitLockRef.current = false;
    }
  }, [
    routeConfig,
    selectedToken,
    sourceAsset,
    destAsset,
    isConnected,
    chainId,
    currentChainId,
    sourceLabel,
    destLabel,
    amount,
    balance,
    address,
    sourceChain,
    destChain,
    netAmount,
    isXrpSource,
    isXrpDestination,
    xrpBridgeWalletAddress,
    bescDestForXrp,
    xrpDestAddress,
    xrpWallet,
    isSolSource,
    isSolTokenSource,
    isSolSplSendSource,
    isSolDestination,
    isSolTokenDestination,
    solBridgeWalletAddress,
    bescDestForSol,
    solDestAddress,
    solWallet,
    sendTransactionAsync,
    writeContractAsync,
    showErrorStatus,
    showFriendlyError,
    showInfoStatus,
    submitBridgeRequest,
    submitPrecheck,
    getFriendlyError,
    isNonceConflictError,
    setProgressStep,
  ]);

  const isBridgeButtonDisabled = isXrpSource
    ? (!xrpWallet.connected || !bescDestForXrp || !/^0x[0-9a-fA-F]{40}$/.test(bescDestForXrp) ||
       !amount || parseFloat(amount) <= 0 || isLoading || !!amountError || Boolean(txHash))
    : isSolSource
      ? (!solWallet.connected || !bescDestForSol || !/^0x[0-9a-fA-F]{40}$/.test(bescDestForSol) ||
         !amount || parseFloat(amount) <= 0 || isLoading || !!amountError || Boolean(txHash))
      : (isSolTokenSource || isSolSplSendSource)
        ? (!solWallet.connected || !bescDestForSol || !/^0x[0-9a-fA-F]{40}$/.test(bescDestForSol) ||
           !amount || parseFloat(amount) <= 0 || isLoading || !!amountError || Boolean(txHash))
        : (!isConnected || !amount || parseFloat(amount) <= 0 || isLoading || !!amountError || Boolean(txHash) ||
           (isXrpDestination && (!xrpDestAddress || !!xrpDestError)) ||
           (isSolDestination && (!solDestAddress || !!solDestError)));

  const openBridgeConfirmation = () => {
    if (isBridgeButtonDisabled) {
      return;
    }
    setActiveSelector(null);
    setShowBridgeConfirm(true);
  };

  const closeBridgeConfirmation = () => {
    setShowBridgeConfirm(false);
  };

  const confirmBridge = () => {
    setShowBridgeConfirm(false);
    handleBridge();
  };

  const closeProgressModal = () => {
    setBridgeProgress((previous) => {
      if (!isProgressClosable(previous)) {
        return previous;
      }

      return {
        ...previous,
        open: false,
      };
    });
  };

  const closeSuccessModal = () => {
    setSuccessState(null);
    if (amountInputRef.current) {
      amountInputRef.current.focus();
    }
  };

  const handleBridgeAgain = () => {
    setSuccessState(null);
    clearStatus();
    if (amountInputRef.current) {
      amountInputRef.current.focus();
    }
  };

  const handleSetMaxAmount = () => {
    if (isXrpSource) {
      if (xrpWallet.balanceDrops !== null) {
        setAmount(formatUnits(xrpWallet.balanceDrops, 6));
      }
      return;
    }
    if (isSolSource) {
      if (solWallet.balanceLamports !== null) {
        setAmount(formatUnits(solWallet.balanceLamports, 9));
      }
      return;
    }
    if (isSolTokenSource) {
      if (solWallet.splBalanceSmallest !== null) {
        setAmount(formatUnits(solWallet.splBalanceSmallest, 9));
      }
      return;
    }
    if (balance && sourceAsset) {
      setAmount(formatUnits(balance.value, sourceAsset.decimals));
    }
  };

  const handleSwapRoute = () => {
    if (!reverseRoute || isInteractionLocked) {
      return;
    }
    setDirection(reverseRoute);
  };

  if (!routeConfig || !selectedToken || !sourceAsset || !destAsset) {
    return (
      <section className="bridge-panel premium-bridge-panel premium-bridge-panel--empty">
        <div className="premium-bridge-panel__header">
          <p className="bridge-panel__tag">Bridge unavailable</p>
          <h2 className="bridge-panel__title">Bridge configuration is incomplete</h2>
          <p className="bridge-panel__subtitle">At least one token route is missing environment configuration.</p>
        </div>
      </section>
    );
  }

  return (
    <>
      <motion.section className="bridge-panel premium-bridge-panel" initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={modalTransition}>
        <div className="premium-bridge-panel__glow" aria-hidden="true" />
        <div className="premium-bridge-panel__mesh" aria-hidden="true" />

        <div className="premium-bridge-panel__header">
          <div>
            <p className="bridge-panel__tag">Cross-chain bridge</p>
            <h2 className="bridge-panel__title">Secure asset transfers across supported networks</h2>
            <p className="bridge-panel__subtitle">Move assets between Ethereum, BNB Chain, XRP Ledger, Solana, and BESC with clear pricing and live progress updates.</p>
          </div>

        </div>

        <div className="premium-bridge-panel__grid">
          <div className="premium-bridge-form" ref={selectorRegionRef}>
            <div className="premium-bridge-connection">
              {(isSolSource || isSolTokenSource) ? (
                solWallet.connected ? (
                  <div className="premium-connected-card">
                    <div>
                      <span className="premium-connected-card__label">
                        {solWallet.walletType === "solflare" ? "Solflare connected" : "Phantom connected"}
                      </span>
                      <strong>{solWallet.address ? `${solWallet.address.slice(0, 8)}...${solWallet.address.slice(-4)}` : "Connected"}</strong>
                    </div>
                    <button type="button" className="secondary-button premium-secondary-button" onClick={() => solWallet.disconnect()} disabled={isInteractionLocked}>
                      Disconnect
                    </button>
                  </div>
                ) : (
                  <div className="premium-sol-connect">
                    {solWallet.phantomAvailable && (
                      <motion.button
                        type="button"
                        className="primary-button premium-primary-button"
                        onClick={async () => {
                          try { await solWallet.connectPhantom(); toast.success("Phantom connected."); }
                          catch (e) { showErrorStatus(e.message || "Phantom connection failed."); }
                        }}
                        disabled={isInteractionLocked}
                        whileHover={{ y: -2, scale: 1.01 }}
                        whileTap={{ scale: 0.99 }}
                      >
                        <Wallet size={18} />
                        Connect Phantom
                      </motion.button>
                    )}
                    {solWallet.solflareAvailable && (
                      <motion.button
                        type="button"
                        className={solWallet.phantomAvailable ? "secondary-button premium-secondary-button" : "primary-button premium-primary-button"}
                        onClick={async () => {
                          try { await solWallet.connectSolflare(); toast.success("Solflare connected."); }
                          catch (e) { showErrorStatus(e.message || "Solflare connection failed."); }
                        }}
                        disabled={isInteractionLocked}
                        whileHover={{ y: -2, scale: 1.01 }}
                        whileTap={{ scale: 0.99 }}
                      >
                        <Wallet size={18} />
                        Connect Solflare
                      </motion.button>
                    )}
                    {!solWallet.phantomAvailable && !solWallet.solflareAvailable && (
                      <div className="premium-sol-nowallet">
                        <span className="premium-connected-card__label">No Solana wallet detected</span>
                        <p>Install <a href="https://phantom.app" target="_blank" rel="noopener noreferrer">Phantom</a> or <a href="https://solflare.com" target="_blank" rel="noopener noreferrer">Solflare</a> to bridge SOL.</p>
                      </div>
                    )}
                  </div>
                )
              ) : isXrpSource ? (
                (xrpWallet.connected || wcXrp.connected) ? (
                  <div className="premium-connected-card">
                    <div>
                      <span className="premium-connected-card__label">
                        {wcXrp.connected ? "WalletConnect"   :
                         xrpWallet.walletType === "xaman"   ? "Xaman connected"         :
                         xrpWallet.walletType === "gemwallet"? "GemWallet connected"     :
                         xrpWallet.walletType === "bescwallet"? "BESC Wallet connected"  :
                         "XRP Wallet connected"}
                      </span>
                      <strong>
                        {(() => { const a = wcXrp.address || xrpWallet.address; return a ? `${a.slice(0,8)}...${a.slice(-4)}` : "Connected"; })()}
                      </strong>
                    </div>
                    <button type="button" className="secondary-button premium-secondary-button"
                      onClick={() => { xrpWallet.disconnect(); wcXrp.disconnect(); }}
                      disabled={isInteractionLocked}>
                      Disconnect
                    </button>
                  </div>
                ) : (
                  <div className="premium-xrp-connect">
                    {/* Single connect button → opens wallet picker */}
                    <motion.button
                      type="button"
                      className="primary-button premium-primary-button premium-primary-button--bridge"
                      onClick={() => setXrpWalletPickerOpen((o) => !o)}
                      disabled={isInteractionLocked}
                      whileHover={{ y: -2, scale: 1.01 }}
                      whileTap={{ scale: 0.99 }}
                    >
                      <Wallet size={18} />
                      Connect XRP Wallet
                    </motion.button>

                    {/* Wallet picker dropdown */}
                    <AnimatePresence>
                      {xrpWalletPickerOpen && (
                        <motion.div
                          className="premium-xrp-picker"
                          initial={{ opacity: 0, y: -6 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0, y: -6 }}
                          transition={{ duration: 0.15 }}
                        >
                          {xrpWallet.bescWalletAvailable && (
                            <button type="button" className="premium-xrp-picker__option" onClick={async () => {
                              setXrpWalletPickerOpen(false);
                              try { await xrpWallet.connectBescWallet(); toast.success("BESC Wallet connected."); }
                              catch (e) { showErrorStatus(e.message || "BESC Wallet connection failed."); }
                            }}>
                              <Wallet size={16} />
                              <span>BESC Wallet</span>
                              <span className="premium-xrp-picker__badge">Detected</span>
                            </button>
                          )}
                          {xrpWallet.gemAvailable && (
                            <button type="button" className="premium-xrp-picker__option" onClick={async () => {
                              setXrpWalletPickerOpen(false);
                              try { await xrpWallet.connectGemWallet(); toast.success("GemWallet connected."); }
                              catch (e) { showErrorStatus(e.message || "GemWallet connection failed."); }
                            }}>
                              <Wallet size={16} />
                              <span>GemWallet</span>
                              <span className="premium-xrp-picker__badge">Detected</span>
                            </button>
                          )}
                          {xrpWallet.xamanAvailable && (
                            <button type="button" className="premium-xrp-picker__option" onClick={async () => {
                              setXrpWalletPickerOpen(false);
                              try { sessionStorage.setItem('xaman_restore', JSON.stringify({ token: tokenKey, direction })); } catch {}
                              try {
                                await xrpWallet.connectXaman();
                                try { sessionStorage.removeItem('xaman_restore'); } catch {}
                                try { sessionStorage.removeItem('xaman_pending'); } catch {}
                                toast.success("Xaman connected.");
                              } catch (e) {
                                try { sessionStorage.removeItem('xaman_restore'); } catch {}
                                try { sessionStorage.removeItem('xaman_pending'); } catch {}
                                showErrorStatus(e.message || "Xaman connection failed.");
                              }
                            }}>
                              <Wallet size={16} />
                              <span>Xaman</span>
                            </button>
                          )}
                          {wcXrp.available && (
                            <button type="button" className="premium-xrp-picker__option" onClick={async () => {
                              setXrpWalletPickerOpen(false);
                              try { await wcXrp.connect(); toast.success("WalletConnect wallet connected."); }
                              catch (e) { showErrorStatus(e.message || "WalletConnect connection failed."); }
                            }}>
                              <Wallet size={16} />
                              <span>WalletConnect</span>
                              <span className="premium-xrp-picker__badge" style={{ color: "#60a5fa", background: "rgba(96,165,250,0.12)" }}>All wallets</span>
                            </button>
                          )}
                          {!xrpWallet.gemAvailable && !xrpWallet.xamanAvailable && !xrpWallet.bescWalletAvailable && !wcXrp.available && (
                            <div className="premium-xrp-picker__empty">
                              <p>No XRP wallet detected.<br />Use <a href="https://xumm.app" target="_blank" rel="noopener noreferrer">Xaman</a> or any <a href="https://walletconnect.com" target="_blank" rel="noopener noreferrer">WalletConnect</a> XRPL wallet.</p>
                            </div>
                          )}
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                )
              ) : !isConnected ? (
                <motion.button
                  type="button"
                  className="primary-button premium-primary-button"
                  onClick={() => connect({ connector: injectedConnector })}
                  disabled={isInteractionLocked}
                  whileHover={{ y: -2, scale: 1.01 }}
                  whileTap={{ scale: 0.99 }}
                >
                  <Wallet size={18} />
                  Connect Wallet
                </motion.button>
              ) : (
                <div className="premium-connected-card">
                  <div>
                    <span className="premium-connected-card__label">Connected wallet</span>
                    <strong>{address ? `${address.slice(0, 6)}...${address.slice(-4)}` : "Wallet connected"}</strong>
                  </div>
                  <button type="button" className="secondary-button premium-secondary-button" onClick={() => disconnect()} disabled={isInteractionLocked}>
                    Disconnect
                  </button>
                </div>
              )}
            </div>

            <div className="premium-selector-grid">
              <div className="premium-field-block">
                <CompactSelect
                  label="Asset"
                  value={allTokens[tokenKey]?.label || tokenKey}
                  isOpen={activeSelector === "asset"}
                  onToggle={() => setActiveSelector((current) => (current === "asset" ? null : "asset"))}
                  onSelect={(key) => {
                    setTokenKey(key);
                    setActiveSelector(null);
                  }}
                  disabled={isInteractionLocked}
                  options={allTokenKeys.map((key) => ({
                    key,
                    title: allTokens[key].label,
                    detail: getAssetCoverageLabel(allTokens[key]),
                    meta: getAssetRouteCountLabel(allTokens[key]),
                    active: tokenKey === key,
                  }))}
                  menuClassName="premium-select__menu--asset"
                />
              </div>

              <div className="premium-field-block">
                <div className="premium-route-line">
                  <CompactSelect
                    label="From"
                    value={sourceLabel}
                    detail={getRouteActionLabel(sourceAsset, "source")}
                    isOpen={activeSelector === "from"}
                    onToggle={() => setActiveSelector((current) => (current === "from" ? null : "from"))}
                    onSelect={(chain) => {
                      const nextRoute = availableRoutes.find((route) => getRouteParts(route).sourceChain === chain);
                      if (nextRoute) {
                        setDirection(nextRoute);
                      }
                      setActiveSelector(null);
                    }}
                    disabled={isInteractionLocked}
                    options={availableFromChains.map((chain) => ({
                      key: chain,
                      title: CHAIN_LABELS[chain],
                      detail: getRouteActionLabel(allTokens[tokenKey]?.chains?.[chain], "source"),
                      meta: CHAIN_SHORT_LABELS[chain],
                      active: sourceChain === chain,
                    }))}
                  />

                  <motion.button
                    type="button"
                    className="premium-swap-button premium-swap-button--inline"
                    onClick={handleSwapRoute}
                    disabled={!reverseRoute || isInteractionLocked}
                    whileTap={!reverseRoute || isInteractionLocked ? undefined : { scale: 0.97 }}
                    aria-label="Switch route"
                  >
                    <span className="premium-swap-button__icon">
                      <ArrowRightLeft size={18} />
                    </span>
                  </motion.button>

                  <CompactSelect
                    label="To"
                    value={destLabel}
                    detail={getRouteActionLabel(destAsset, "destination")}
                    isOpen={activeSelector === "to"}
                    onToggle={() => setActiveSelector((current) => (current === "to" ? null : "to"))}
                    onSelect={(chain) => {
                      const nextRoute = availableRoutes.find((route) => {
                        const parts = getRouteParts(route);
                        return parts.sourceChain === sourceChain && parts.destChain === chain;
                      });
                      if (nextRoute) {
                        setDirection(nextRoute);
                      }
                      setActiveSelector(null);
                    }}
                    disabled={isInteractionLocked}
                    options={availableToChains.map((chain) => ({
                      key: chain,
                      title: CHAIN_LABELS[chain],
                      detail: getRouteActionLabel(allTokens[tokenKey]?.chains?.[chain], "destination"),
                      meta: CHAIN_SHORT_LABELS[chain],
                      active: destChain === chain,
                    }))}
                  />
                </div>
              </div>
            </div>

            <div className="premium-field-block">
              <div className="premium-amount-card">
                <div className="premium-amount-card__head">
                  <span className="section-title">Amount</span>
                  <span className="premium-balance-chip">Balance: {balanceText}</span>
                </div>

                <div className="premium-amount-card__controls">
                  <input
                    type="number"
                    className="form-input premium-amount-input"
                    value={amount}
                    onChange={(event) => setAmount(event.target.value)}
                    placeholder="0.0"
                    disabled={isInteractionLocked}
                    min="0"
                    step={sourceAsset.decimals === 18 ? "0.000000000000000001" : "0.000000001"}
                    ref={amountInputRef}
                  />

                  <div className="premium-amount-card__actions">
                    <span className="premium-token-pill">{sourceAsset.symbol}</span>
                    {(isXrpSource ? xrpWallet.connected : isSolSource ? solWallet.connected : (isConnected && balance)) && (
                      <button
                        type="button"
                        className="secondary-button mini premium-secondary-button premium-secondary-button--mini"
                        onClick={handleSetMaxAmount}
                        disabled={isInteractionLocked}
                      >
                        Max
                      </button>
                    )}
                  </div>
                </div>

                <AnimatePresence mode="wait">
                  {amountError ? (
                    <motion.p
                      key="amount-error"
                      className="field-error premium-inline-message premium-inline-message--error"
                      initial={{ opacity: 0, y: 8 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -6 }}
                    >
                      {amountError}
                    </motion.p>
                  ) : (
                    <motion.div
                      key={`estimate-${receiveEstimate}`}
                      className="premium-receive-estimate"
                      initial={{ opacity: 0, y: 8 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -6 }}
                    >
                      {bridgeQuote && sourceAsset && destAsset && (bridgeQuote.taxRaw > 0n || bridgeQuote.feeRaw > 0n || bridgeQuote.destTaxRaw > 0n) && (
                        <div className="bridge-fee-breakdown">
                          {bridgeQuote.taxRaw > 0n && (
                            <div className="bridge-fee-breakdown__row">
                              <span>Token tax ({sourceAsset.sourceTokenTaxBps / 100}%)</span>
                              <span>−{formatDisplayUnits(bridgeQuote.taxRaw, sourceAsset.decimals)} {sourceAsset.symbol}</span>
                            </div>
                          )}
                          {bridgeQuote.feeRaw > 0n && (
                            <div className="bridge-fee-breakdown__row">
                              <span>Bridge fee ({Number(allTokens[tokenKey]?.bridgeFeeBps || 10) / 100}%)</span>
                              <span>−{formatDisplayUnits(bridgeQuote.feeRaw, sourceAsset.decimals)} {sourceAsset.symbol}</span>
                            </div>
                          )}
                          {bridgeQuote.destTaxRaw > 0n && (
                            <div className="bridge-fee-breakdown__row">
                              <span>Dest. token tax ({destAsset.destTokenTaxBps / 100}%)</span>
                              <span>−{formatDisplayUnits(bridgeQuote.destTaxRaw, destAsset.decimals)} {destAsset.symbol}</span>
                            </div>
                          )}
                        </div>
                      )}
                      <div className="bridge-fee-breakdown__total">
                        <span>Estimated receive</span>
                        <strong>
                          {receiveEstimate} {netAmount ? destAsset.symbol : ""}
                        </strong>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </div>

            {/* SOL destination address — shown when bridging BESC → SOL */}
            {isSolDestination && (
              <div className="premium-field-block">
                <div className="premium-amount-card">
                  <div className="premium-amount-card__head">
                    <span className="section-title">SOL Destination Address</span>
                  </div>
                  <div className="premium-amount-card__controls">
                    <input
                      type="text"
                      className="form-input premium-amount-input"
                      value={solDestAddress}
                      onChange={(e) => setSolDestAddress(e.target.value.trim())}
                      placeholder="Solana address..."
                      disabled={isInteractionLocked}
                      spellCheck={false}
                      autoComplete="off"
                    />
                  </div>
                  <AnimatePresence mode="wait">
                    {solDestError ? (
                      <motion.p key="sol-dest-error" className="field-error premium-inline-message premium-inline-message--error"
                        initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -6 }}>
                        {solDestError}
                      </motion.p>
                    ) : solDestAddress && !solDestError ? (
                      <motion.p key="sol-dest-ok" className="premium-inline-message premium-inline-message--success"
                        initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -6 }}>
                        Valid Solana address
                      </motion.p>
                    ) : null}
                  </AnimatePresence>
                </div>
              </div>
            )}

            {/* XRP destination address — shown when bridging BESC → XRP */}
            {isXrpDestination && (
              <div className="premium-field-block">
                <div className="premium-amount-card">
                  <div className="premium-amount-card__head">
                    <span className="section-title">XRP Destination Address</span>
                  </div>
                  <div className="premium-amount-card__controls">
                    <input
                      type="text"
                      className="form-input premium-amount-input"
                      value={xrpDestAddress}
                      onChange={(e) => setXrpDestAddress(e.target.value.trim())}
                      placeholder="r..."
                      disabled={isInteractionLocked}
                      spellCheck={false}
                      autoComplete="off"
                    />
                  </div>
                  <AnimatePresence mode="wait">
                    {xrpDestError ? (
                      <motion.p key="xrp-dest-error" className="field-error premium-inline-message premium-inline-message--error"
                        initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -6 }}>
                        {xrpDestError}
                      </motion.p>
                    ) : xrpDestAddress && !xrpDestError ? (
                      <motion.p key="xrp-dest-ok" className="premium-inline-message premium-inline-message--success"
                        initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -6 }}>
                        Valid XRP address
                      </motion.p>
                    ) : null}
                  </AnimatePresence>
                </div>
              </div>
            )}

            {/* XRPtoBESC: BESC destination */}
            {isXrpSource && (
              <div className="premium-field-block">
                <div className="premium-amount-card">
                  <div className="premium-amount-card__head">
                    <span className="section-title">Your BESC Destination Address</span>
                  </div>
                  <div className="premium-amount-card__controls">
                    <input
                      type="text"
                      className="form-input premium-amount-input"
                      value={bescDestOverride}
                      onChange={(e) => setBescDestOverride(e.target.value.trim())}
                      placeholder="0x..."
                      disabled={isInteractionLocked}
                      spellCheck={false}
                      autoComplete="off"
                    />
                  </div>
                  <AnimatePresence mode="wait">
                    {bescDestOverride && !/^0x[0-9a-fA-F]{40}$/.test(bescDestOverride) ? (
                      <motion.p key="besc-dest-err" className="field-error premium-inline-message premium-inline-message--error"
                        initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -6 }}>
                        Enter a valid BESC (0x) address.
                      </motion.p>
                    ) : bescDestOverride ? (
                      <motion.p key="besc-dest-ok" className="premium-inline-message premium-inline-message--success"
                        initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -6 }}>
                        {destAsset?.symbol || "WXRP"} will be minted to this address.
                      </motion.p>
                    ) : (
                      <motion.p key="besc-dest-hint" className="premium-receive-estimate"
                        initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -6 }}>
                        <span>Enter your BESC HyperChain address to receive {destAsset?.symbol || "WXRP"}</span>
                      </motion.p>
                    )}
                  </AnimatePresence>
                </div>
              </div>
            )}

            {/* SOL→BESC: destination EVM/BESC address (WSOL native, WBESC SPL burns, custom SPL tokens) */}
            {(isSolSource || isSolTokenSource || isSolSplSendSource) && (
              <div className="premium-field-block">
                <div className="premium-amount-card">
                  <div className="premium-amount-card__head">
                    <span className="section-title">Your {destLabel || "EVM"} Destination Address</span>
                  </div>
                  <div className="premium-amount-card__controls">
                    <input
                      type="text"
                      className="form-input premium-amount-input"
                      value={bescDestSolOverride}
                      onChange={(e) => setBescDestSolOverride(e.target.value.trim())}
                      placeholder="0x..."
                      disabled={isInteractionLocked}
                      spellCheck={false}
                      autoComplete="off"
                    />
                  </div>
                  <AnimatePresence mode="wait">
                    {bescDestSolOverride && !/^0x[0-9a-fA-F]{40}$/.test(bescDestSolOverride) ? (
                      <motion.p key="besc-sol-dest-err" className="field-error premium-inline-message premium-inline-message--error"
                        initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -6 }}>
                        Enter a valid EVM (0x) address.
                      </motion.p>
                    ) : bescDestSolOverride ? (
                      <motion.p key="besc-sol-dest-ok" className="premium-inline-message premium-inline-message--success"
                        initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -6 }}>
                        {destAsset?.symbol || "tokens"} will be delivered to this address.
                      </motion.p>
                    ) : (
                      <motion.p key="besc-sol-dest-hint" className="premium-receive-estimate"
                        initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -6 }}>
                        <span>Enter your {destLabel || "destination"} address to receive {destAsset?.symbol || "tokens"}</span>
                      </motion.p>
                    )}
                  </AnimatePresence>
                </div>
              </div>
            )}

            {isConnected && !isExternalChainSource && chainId !== currentChainId && (
              <button type="button" className="secondary-button premium-secondary-button premium-secondary-button--block" onClick={handleSwitchChain} disabled={isInteractionLocked}>
                Switch to {sourceLabel}
              </button>
            )}

            {xamanSigningUrl && (
              <AnimatePresence>
                <motion.a
                  ref={xamanLinkRef}
                  href={xamanSigningUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="primary-button premium-primary-button premium-primary-button--bridge"
                  style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 8, textDecoration: "none", animation: "premiumPulse 1.8s ease-in-out infinite" }}
                  initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -6 }}
                  whileHover={{ y: -2, scale: 1.01 }}
                  whileTap={{ scale: 0.99 }}
                >
                  <Wallet size={18} />
                  Open in Xaman to approve
                </motion.a>
              </AnimatePresence>
            )}

            <div className="bridge-panel__actions premium-bridge-actions">
              <motion.button
                type="button"
                className="primary-button premium-primary-button premium-primary-button--bridge"
                disabled={isBridgeButtonDisabled}
                onClick={openBridgeConfirmation}
                whileHover={isBridgeButtonDisabled ? undefined : { y: -2, scale: 1.01 }}
                whileTap={isBridgeButtonDisabled ? undefined : { scale: 0.99 }}
              >
                {isLoading ? (
                  <>
                    <span className="bridge-loading-spinner" aria-hidden="true" />
                    Processing bridge...
                  </>
                ) : (
                  `Review & Bridge ${amount || "0"} ${sourceAsset.symbol}`
                )}
              </motion.button>
            </div>

            <AnimatePresence>
              {!showBridgeConfirm && !bridgeProgress.open && !successState && status && (
                <motion.div
                  className={`status-banner status-banner--${statusTone} premium-status-banner`}
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -8 }}
                >
                  <span className="status-banner__message">{status}</span>
                  {statusDetail && <small className="status-detail">{statusDetail}</small>}
                </motion.div>
              )}
            </AnimatePresence>

            {!showBridgeConfirm && !bridgeProgress.open && !successState && txHash && (
              <a href={getExplorerLink(sourceChain, txHash)} target="_blank" rel="noopener noreferrer" className="status-link premium-status-link">
                View source transaction
                <ExternalLink size={14} />
              </a>
            )}
          </div>
        </div>
      </motion.section>

      <AnimatePresence>
        {showBridgeConfirm && (
          <motion.div
            className="bridge-modal-overlay"
            role="alertdialog"
            aria-modal="true"
            aria-labelledby="bridge-confirm-title"
            onClick={(event) => {
              if (event.target === event.currentTarget) {
                closeBridgeConfirmation();
              }
            }}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <motion.div
              className="bridge-modal-card bridge-modal-card--confirm"
              onClick={(event) => event.stopPropagation()}
              initial={{ opacity: 0, y: 24, scale: 0.98 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 16, scale: 0.98 }}
              transition={modalTransition}
            >
              <button type="button" className="bridge-modal-close" onClick={closeBridgeConfirmation} aria-label="Close confirmation panel">
                <X size={18} />
              </button>

              <div className="bridge-modal-card__head">
                <span className="bridge-modal-pill">Ready to bridge</span>
                <h3 id="bridge-confirm-title">Confirm this transfer</h3>
                <p>Review the route and amount before submitting the wallet action. The destination route cannot be changed mid-transfer.</p>
              </div>

              <div className="bridge-success-grid">
                <div className="bridge-success-item">
                  <span>Asset</span>
                  <strong>{selectedToken.label}</strong>
                </div>
                <div className="bridge-success-item">
                  <span>Route</span>
                  <strong>
                    {sourceLabel} to {destLabel}
                  </strong>
                </div>
                <div className="bridge-success-item">
                  <span>You send</span>
                  <strong>
                    {amount || "0"} {sourceAsset.symbol}
                  </strong>
                </div>
                {bridgeQuote?.taxRaw > 0n && sourceAsset && (
                  <div className="bridge-success-item bridge-success-item--fee">
                    <span>Token tax ({sourceAsset.sourceTokenTaxBps / 100}%)</span>
                    <strong>−{formatDisplayUnits(bridgeQuote.taxRaw, sourceAsset.decimals)} {sourceAsset.symbol}</strong>
                  </div>
                )}
                {bridgeQuote?.feeRaw > 0n && sourceAsset && (
                  <div className="bridge-success-item bridge-success-item--fee">
                    <span>Bridge fee ({Number(allTokens[tokenKey]?.bridgeFeeBps || 10) / 100}%)</span>
                    <strong>−{formatDisplayUnits(bridgeQuote.feeRaw, sourceAsset.decimals)} {sourceAsset.symbol}</strong>
                  </div>
                )}
                {bridgeQuote?.destTaxRaw > 0n && destAsset && (
                  <div className="bridge-success-item bridge-success-item--fee">
                    <span>Dest. token tax ({destAsset.destTokenTaxBps / 100}%)</span>
                    <strong>−{formatDisplayUnits(bridgeQuote.destTaxRaw, destAsset.decimals)} {destAsset.symbol}</strong>
                  </div>
                )}
                <div className="bridge-success-item">
                  <span>Estimated receive</span>
                  <strong>
                    {receiveEstimate} {netAmount ? destAsset.symbol : ""}
                  </strong>
                </div>
              </div>

              <div className="bridge-confirm-route-card">
                <span className="bridge-confirm-route-card__eyebrow">Bridge route</span>
                <div className="bridge-confirm-route-card__line">
                  <strong>{sourceLabel}</strong>
                  <span className="bridge-confirm-route-card__arrow">to</span>
                  <strong>{destLabel}</strong>
                </div>
                <p>{selectedToken.label} transfer with live delivery updates shown after wallet approval.</p>
              </div>

              <p className="bridge-confirm-note">Once approved, you will see live transfer updates until delivery is complete.</p>

              <div className="bridge-confirm-actions">
                <button type="button" className="secondary-button premium-secondary-button" onClick={closeBridgeConfirmation}>
                  Go Back
                </button>
                <button type="button" className="primary-button premium-primary-button" onClick={confirmBridge} disabled={isLoading}>
                  Confirm & Bridge
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {bridgeProgress.open && (
          <BridgeProgressModal
            progress={bridgeProgress}
            sourceChain={sourceChain}
            destChain={destChain}
            sourceLabel={sourceLabel}
            destLabel={destLabel}
            tokenLabel={selectedToken.label}
            sourceSymbol={sourceAsset.symbol}
            destinationSymbol={destAsset.symbol}
            amountText={amount || "0"}
            receiveText={receiveEstimate}
            onClose={closeProgressModal}
          />
        )}
      </AnimatePresence>

      <AnimatePresence>
        {successState && <BridgeSuccessModal successState={successState} onRestart={handleBridgeAgain} onClose={closeSuccessModal} />}
      </AnimatePresence>
    </>
  );
};

export default BridgeWidget;
